import { Button } from "../../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../../components/ui/card";
import { Checkbox } from "../../../../../../components/ui/checkbox";
import { Input } from "../../../../../../components/ui/input";
import { Label } from "../../../../../../components/ui/label";
import { MultiSelect } from "../../../../../../components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "../../../../../../components/ui/dialog";
import { Tooltip } from "../../../../../../components/ui/tooltip";
import { QrCode, Ticket, ChevronLeft, Save, AlertTriangle, Download, Plus, Trash2, Settings } from "lucide-react";
import { SpecificationDocument, TeamMember } from "../../../../types";
import { useTeamMembers } from "../../../../../../redux/ProductionData/hooks/useTeamMembers";
import { useState, useEffect, useRef } from "react";
import { Alignment } from "./Alignment";
import { VisualInspection } from "./VisualInspection";
import { Fitment } from "./Fitment";
import { QRScanner } from "../QRScanner";
import { convertSetupValue, convertSetupValueToInches, getUnitFromSettings, getUnitLabel } from "../../../../../../utils/unitUtils";
import { Signature } from "./Signature";
import {
  updateAssemblyStatus, updateAssemblyFitment, updateAssemblyVisualInspection, updateAssemblyAlignment, fetchProductionData, setProductionData, clearProductionData, downloadFitmentReport, clearFitmentReportDownload, downloadProductionSheetMainReport, clearProductionSheetMainReportDownload,
  clearVisualInspectionReportDownload, downloadAlignmentReport, clearAlignmentReportDownload, downloadVisualInspectionReport, deleteRepairWeld
} from "../../../../../../redux/ProductionData/actions";
import { useDispatch, useSelector } from "react-redux";
import { AntdDate } from "../../../../../../components/ui/antd-date";
import dayjs from "dayjs";
import { Notification } from "../../../../../../components/ui/custom-notification";
import { DocumentApprovalModal } from '../DocumentApprovalModal';
import axiosConfig from "../../../../../../redux/ProductionData/axiosConfig";
import { PdfViewer } from '../../../../../../components/viewers/PdfViewer';
import { MediaViewer } from '../../../../../../components/viewers/MediaViewer';
import { DocumentViewer } from '../../../../../../components/viewers/DocumentViewer';
import { DeleteConfirmationDialog } from '../../../../../../components/ui/DeleteConfirmationDialog';
import { SearchableSelectDropdown } from '../../../../../../components/ui/SearchableSelectDropdown';

interface ProductionTabProps {
  specifications: SpecificationDocument[];
  materials: {
    pipes?: any[];
    forge?: any[];
    gas?: any[];
    wireLot?: any[];
    coating?: any[];
  };
  selectedPipeSize: number;

  selectedAssembly: string | null;
  setSelectedAssembly: (assembly: string | null) => void;
  onBack: () => void;
  selectedPipe: any;
  selectedItem: any;
  project: any;
  canEdit?: boolean;
  canReport?: boolean;
  canSign?: boolean;
  isCustomerRepresentativeUser?: boolean;
  isQCInspectorUser?: boolean;
  isWelderUser?: boolean;
  currentUserTeamMemberId?: string | number;
  isAdminOrSuperAdmin?: boolean;
  refreshMaterials: () => void;
}

interface FitmentData {
  refLocation: number;
  landEdgePipe: number;
  landEdgeForging: number;
  landEdgeDifference: number;
  measuredLandHiLo: number;
  calculatedInternalHiLo: number;
  wallThicknessPipe: number;
  wallThicknessForging: number;
  calculatedExternalHiLo: number;
  bevelTopRange: number;
}



interface FitmentState {
  fitmentData: FitmentData[];
  toleranceLandMax: number;
  toleranceLandMin: number;
  toleranceWallThicknessMax: number;
  toleranceWallThicknessMin: number;
  numberOfReadings: number;
  notes: string;
  customerSignature: string | null;
  qcSignature: string | null;
}

export const ProductionTabV1: React.FC<ProductionTabProps> = ({
  specifications,
  selectedPipeSize,
  selectedAssembly,
  setSelectedAssembly,
  onBack,
  selectedPipe,
  materials,
  selectedItem,
  project,
  canEdit = false,
  canReport = false,
  canSign = false,
  isCustomerRepresentativeUser = false,
  isQCInspectorUser = false,
  isWelderUser = false,
  currentUserTeamMemberId,
  isAdminOrSuperAdmin = false,
  refreshMaterials
}) => {
  const { teamMembers, loadTeamMembers, teamMembersLoading, teamMembersError } = useTeamMembers();
  const dispatch = useDispatch();

  // Get Redux state for save status
  const statusUpdateLoading = useSelector((state: any) => state.productionData?.statusUpdateLoading);
  const statusUpdateError = useSelector((state: any) => state.productionData?.statusUpdateError);
  const statusUpdateSuccess = useSelector((state: any) => state.productionData?.statusUpdateSuccess);

  // Get Redux state for fitment save status
  const fitmentUpdateLoading = useSelector((state: any) => state.productionData?.fitmentUpdateLoading);
  const fitmentUpdateError = useSelector((state: any) => state.productionData?.fitmentUpdateError);
  const fitmentUpdateSuccess = useSelector((state: any) => state.productionData?.fitmentUpdateSuccess);

  // Get Redux state for production sheet main report download
  const productionSheetMainReportDownloadLoading = useSelector((state: any) => state.productionData?.productionSheetMainReportDownloadLoading);
  const productionSheetMainReportDownloadData = useSelector((state: any) => state.productionData?.productionSheetMainReportDownloadData);
  const productionSheetMainReportDownloadError = useSelector((state: any) => state.productionData?.productionSheetMainReportDownloadError);


  // Get Redux state for fitment save status
  const visualInspectionUpdateLoading = useSelector((state: any) => state.productionData?.visualInspectionUpdateLoading);
  const visualInspectionUpdateError = useSelector((state: any) => state.productionData?.visualInspectionUpdateError);
  const visualInspectionUpdateSuccess = useSelector((state: any) => state.productionData?.visualInspectionUpdateSuccess);

  // Get Redux state for fitment save status
  const alignmentUpdateLoading = useSelector((state: any) => state.productionData?.alignmentUpdateLoading);
  const alignmentUpdateError = useSelector((state: any) => state.productionData?.alignmentUpdateError);
  const alignmentUpdateSuccess = useSelector((state: any) => state.productionData?.alignmentUpdateSuccess);

  // Get Redux state for production data
  const productionData = useSelector((state: any) => state.productionData?.productionData);
  const productionDataLoading = useSelector((state: any) => state.productionData?.productionDataLoading);
  const productionDataError = useSelector((state: any) => state.productionData?.productionDataError);
  const productionDataSuccess = useSelector((state: any) => state.productionData?.productionDataSuccess);

  // Get Redux state for fitment report download
  const fitmentReportDownloadLoading = useSelector((state: any) => state.productionData?.fitmentReportDownloadLoading);
  const fitmentReportDownloadData = useSelector((state: any) => state.productionData?.fitmentReportDownloadData);
  const fitmentReportDownloadError = useSelector((state: any) => state.productionData?.fitmentReportDownloadError);
  const [selectedGasIds, setSelectedGasIds] = useState<string[]>([]);
  const [selectedWireIds, setSelectedWireIds] = useState<string[]>([]);
  const [customerSignature, setCustomerSignature] = useState<string | null>(null);
  const [qcSignature, setQcSignature] = useState<string | null>(null);
  const [selectedForgeId, setSelectedForgeId] = useState<string | null>(null);
  const [forgeSearchTerm, setForgeSearchTerm] = useState<string>('');
  const [isForgeSelectOpen, setIsForgeSelectOpen] = useState<boolean>(false);
  const [selectedPipeId, setSelectedPipeId] = useState<string | null>(null);
  const [isPipeSelectOpen, setIsPipeSelectOpen] = useState<boolean>(false);
  const [distanceFromPipeEnd, setDistanceFromPipeEnd] = useState<number | null>(null);
  const [selectedWeld, setSelectedWeld] = useState<number>(0);
  const [selectedPassConfigurationId, setSelectedPassConfigurationId] = useState<string | null>(null);
  const [isPassConfigurationSaved, setIsPassConfigurationSaved] = useState(false);
  const [passConfigurationLastUpdated, setPassConfigurationLastUpdated] = useState<Date | null>(null);

  // Ref to track last processed production data to prevent duplicate processing
  const lastProcessedDataRef = useRef<string | null>(null);

  // Add team member list state variables
  const [welderList, setWelderList] = useState<Array<{ value: string, label: string, name: string }>>([]);
  const [qcList, setQcList] = useState<Array<{ value: string, label: string, name: string }>>([]);
  const [managerList, setManagerList] = useState<Array<{ value: string, label: string }>>([]);
  const [supervisorList, setSupervisorList] = useState<Array<{ value: string, label: string }>>([]);
  const [customerRepresentativeList, setCustomerRepresentativeList] = useState<Array<{ value: string, label: string, name: string }>>([]);
  const [repairAlignments, setRepairAlignments] = useState<any>([]);

  // Add welder selection state
  const [selectedWelder1, setSelectedWelder1] = useState<string[]>([]);
  const [selectedWelder2, setSelectedWelder2] = useState<string[]>([]);
  const [selectedCustomerRepresentativeId, setSelectedCustomerRepresentativeId] = useState<string | null>(null);
  const [selectedQcInspectorId, setSelectedQcInspectorId] = useState<string | null>(null);
  const [fitmentWeldData, setFitmentWeldData] = useState<any>(null);
  const [visualInspectionWeldData, setVisualInspectionWeldData] = useState<any>(null);
  const [alignmentWeldData, setAlignmentWeldData] = useState<any>(null);
  const [selectedHeadings, setSelectedHeadings] = useState<any>(null);
  const [isSaveLoading, setIsSaveLoading] = useState(false);



  const [currentView, setCurrentView] = useState<'main' | 'fitment' | 'visual' | 'alignment'>('main');
  const [isRepairAlignmentView, setIsRepairAlignmentView] = useState(false);
  const [selectedRepairIndex, setSelectedRepairIndex] = useState<number | null>(null);
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [isQRGeneratorOpen, setIsQRGeneratorOpen] = useState(false);
  const [scanningFor, setScanningFor] = useState<'forge' | 'pipe' | 'weld1' | 'weld2' | string | null>(null);
  const [unit, setUnit] = useState<string>('Inches');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [showAllInfo, setShowAllInfo] = useState(false);
  const [showEditConfirmation, setShowEditConfirmation] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isProductionSheetEditMode, setIsProductionSheetEditMode] = useState(true);
  const [passConfigurationWeld_1, setPassConfigurationWeld_1] = useState<any>([]);
  const [passConfigurationWeld_2, setPassConfigurationWeld_2] = useState<any>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [productionSheetData, setProductionSheetData] = useState<any>(null);
  const [isDocumentApprovalModalOpen, setIsDocumentApprovalModalOpen] = useState(false);
  const [weld1Name, setweld1Name] = useState('Weld-001')
  const [weld2Name, setweld2Name] = useState('Weld-002')
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [numberOfWelds, setNumberOfWelds] = useState(1);

  // Per-weld section: Pipe NRI and Pass Configuration selection (used in Weld Section dialog)
  const [weld1PipeId, setWeld1PipeId] = useState<string | null>(null);
  const [weld2PipeId, setWeld2PipeId] = useState<string | null>(null);
  const [weld1PassConfigId, setWeld1PassConfigId] = useState<string | null>(null);
  const [weld2PassConfigId, setWeld2PassConfigId] = useState<string | null>(null);
  const [weld1FitmentId, setWeld1FitmentId] = useState<string | null>(null);
  const [weld2FitmentId, setWeld2FitmentId] = useState<string | null>(null);
  const [weldSectionDialogWeldIndex, setWeldSectionDialogWeldIndex] = useState<1 | 2 | null>(null);
  const [weldSectionDialogRepairIndex, setWeldSectionDialogRepairIndex] = useState<number>(0);
  // Pending state in Weld Section dialog (applied only on Apply, not on select)
  const [weldSectionPendingPipeId, setWeldSectionPendingPipeId] = useState<string | null>(null);
  const [weldSectionPendingPassConfigId, setWeldSectionPendingPassConfigId] = useState<string | null>(null);
  const [weldSectionPendingFitmentId, setWeldSectionPendingFitmentId] = useState<string | null>(null);

  // State for repair welds: Map<`${weldindex}-${repairindex}`, {weldName, passConfiguration, materialPipeId?, passConfigDocId?, fitmentSpecDocId?, productionWeldId?}>
  const [repairWelds, setRepairWelds] = useState<Map<string, { weldName: string, passConfiguration: any[], materialPipeId?: string | null, passConfigDocId?: string | null, fitmentSpecDocId?: string | null, productionWeldId?: number | null }>>(new Map());
  const [numberOfRepairsWeld1, setNumberOfRepairsWeld1] = useState(0);
  const [numberOfRepairsWeld2, setNumberOfRepairsWeld2] = useState(0);
  const [isDeleteRepairWeldModalVisible, setIsDeleteRepairWeldModalVisible] = useState(false);
  const [itemToDeleteRepairWeld, setItemToDeleteRepairWeld] = useState<any | null>(null);
  const [deleteRepairWeldLoading, setDeleteRepairWeldLoading] = useState(false);

  // Viewer state management
  const [viewerType, setViewerType] = useState<'pdf' | 'media' | 'document' | null>(null);
  const [viewerFile, setViewerFile] = useState<any | null>(null);
  const [viewerFiles, setViewerFiles] = useState<any[]>([]);

  // Add fitment state management
  const [fitmentState, setFitmentState] = useState<FitmentState>({
    fitmentData: [],
    toleranceLandMax: 0.5,
    toleranceLandMin: 0.75,
    toleranceWallThicknessMax: 1.0,
    toleranceWallThicknessMin: 2.0,
    numberOfReadings: 0,
    notes: "",
    customerSignature: null,
    qcSignature: null,
  });

  // Add visual inspection state management
  const [visualInspectionState, setVisualInspectionState] = useState<any>({
    notes: "",
    customerSignature: null,
    qcSignature: null,
    weldPasses: []
  });

  // Add alignment state management
  const [alignmentState, setAlignmentState] = useState<any>({
    notes: "",
    customerSignature: null,
    qcSignature: null,
    alignmentReadings: [],
    axialAlignment0deg: 0,
    axialAlignment90deg: 0,
    lengthEndToEnd: 0,
    verificationStatement: ""
  });



  // Listen for save success/failure
  useEffect(() => {
    if (statusUpdateError) {
      setIsSaveLoading(false)
      Notification.destroy();
      Notification.error(statusUpdateError || 'Failed to save production data');
    }
  }, [statusUpdateError]);

  // Listen for save success
  useEffect(() => {
    if (statusUpdateSuccess && isSaveLoading) {
      //  Notification.destroy();
      //   Notification.success('Production data saved successfully');

      if (hasProductionSheetSignatures()) {
        handleDownloadProductionSheetMainReport();
      }
      
      refreshMaterials();
      //   const signaturesExist = productionSheetData?.signature?.qc?.signature && productionSheetData?.signature?.customer?.signature;
      // console.log('signaturesExist', signaturesExist);
      //  setIsProductionSheetEditMode(!signaturesExist);
      setIsSaveLoading(false)
      loadProductionData();
      setHasUnsavedChanges(false);
      // // Mark pass configuration as saved after successful save
      // if (selectedPassConfigurationId) {
      //   setIsPassConfigurationSaved(true);
      // }
    }
  }, [statusUpdateSuccess]);

  // Listen for fitment save success/failure
  useEffect(() => {
    if (fitmentUpdateError || visualInspectionUpdateError || alignmentUpdateError) {
      setIsSaveLoading(false)
      Notification.error(fitmentUpdateError || visualInspectionUpdateError || alignmentUpdateError || 'Failed to save data');
    }
  }, [fitmentUpdateError, visualInspectionUpdateError, alignmentUpdateError]);

  // Listen for fitment save success
  useEffect(() => {
    if (fitmentUpdateSuccess && isSaveLoading) {
      if (hasFitmentSignatures() && fitmentWeldData) {
        handleDownloadFitmentReport(fitmentWeldData);
      }
      setIsSaveLoading(false)
      loadProductionData();
      setHasUnsavedChanges(false);
      // Reset edit mode after save - check if signatures exist
      const signaturesExist = fitmentWeldData?.signature?.qc?.signature && fitmentWeldData?.signature?.customer?.signature;
      // console.log('signaturesExist', signaturesExist);
      setIsEditMode(!signaturesExist);
    }
  }, [fitmentUpdateSuccess]);
  // Listen for visual inspection save success
  useEffect(() => {
    if (visualInspectionUpdateSuccess && isSaveLoading) {
      if (hasVisualInspectionSignatures() && visualInspectionWeldData) {
        handleDownloadVisualInspectionReport(visualInspectionWeldData);
      }
      setIsSaveLoading(false)
      loadProductionData();

      setHasUnsavedChanges(false);
      // Reset edit mode after save - check if signatures exist
      const signaturesExist = visualInspectionWeldData?.signature?.qc?.signature && visualInspectionWeldData?.signature?.customer?.signature;
      setIsEditMode(!signaturesExist);
    }
  }, [visualInspectionUpdateSuccess]);


  useEffect(() => {
    if (alignmentUpdateSuccess && isSaveLoading) {
      if (hasAlignmentSignatures() && alignmentWeldData) {
        handleDownloadAlignmentReport();
      }
      setIsSaveLoading(false)
      loadProductionData();
      setHasUnsavedChanges(false);
      // Reset edit mode after save - check if signatures exist
      const signaturesExist = alignmentWeldData?.signature?.qc?.signature && alignmentWeldData?.signature?.customer?.signature;
      setIsEditMode(!signaturesExist);
    }
  }, [alignmentUpdateSuccess]);


  // // Auto-generate fitment report after successful save if signatures are available
  // useEffect(() => {

  // }, [fitmentUpdateSuccess]);

  // // Auto-generate visual inspection report after successful save if signatures are available
  // useEffect(() => {
  //   if (visualInspectionUpdateSuccess && hasVisualInspectionSignatures() && visualInspectionWeldData) {
  //     handleDownloadVisualInspectionReport(visualInspectionWeldData);
  //   }
  // }, [visualInspectionUpdateSuccess]);

  // // Auto-generate alignment report after successful save if signatures are available
  // useEffect(() => {
  //   if (alignmentUpdateSuccess && hasAlignmentSignatures() && alignmentWeldData) {
  //     handleDownloadAlignmentReport();
  //   }
  // }, [alignmentUpdateSuccess]);



  // Handle download fitment report
  const handleDownloadFitmentReport = async (weldData: any) => {
    // Validate required data
    if (!selectedItem?.projectId || !selectedPipe?.projectId) {
      Notification.error('Project ID not found');
      return;
    }
    if (!selectedItem?.pipeId || !selectedPipe?.pipeId) {
      Notification.error('Pipe ID not found');
      return;
    }
    if (!weldData?.productionId) {
      Notification.error('Production ID not found');
      return;
    }
    if (!weldData?.productionWeldFitmentId) {
      Notification.error('Fitment ID not found');
      return;
    }
    if (!weldData?.productionWeldId) {
      Notification.error('Weld ID not found');
      return;
    }
    setIsReportLoading(true);

    const payload = {
      projectId: selectedItem.projectId || selectedPipe.projectId,
      pipeId: selectedItem.pipeId || selectedPipe.pipeId,
      productionId: weldData.productionId,
      productionWeldFitmentId: weldData.productionWeldFitmentId,
      productionWeldId: weldData.productionWeldId
    };

    // // Dispatch action to set loading state
    // dispatch(downloadFitmentReport(payload));

    // Call API directly and handle download in .then()
    try {
      const params = new URLSearchParams({
        projectId: payload.projectId.toString(),
        pipeId: payload.pipeId.toString(),
        productionId: payload.productionId.toString(),
        productionWeldFitmentId: payload.productionWeldFitmentId.toString(),
        productionWeldId: payload.productionWeldId.toString()
      });

      const url = `/download-proxy/reports/joint-fit-up-inspection?${params.toString()}`;
      const res = await axiosConfig.get(url);

      if (res.status === 200 && res.data.success === 1 && res.data.data?.downloadUrl) {


        loadProductionData();
        // // Download the PDF by opening in new tab
        // const link = document.createElement('a');
        // link.href = res.data.data.downloadUrl;
        // link.target = '_blank';
        // link.rel = 'noopener noreferrer';
        // document.body.appendChild(link);
        // link.click();
        // document.body.removeChild(link);
      } else {
        Notification.error('Failed to generate report');
      }
    } catch (error) {
      Notification.error('Error downloading report');
    } finally {
      // Clear download state
      dispatch(clearFitmentReportDownload());
      setIsReportLoading(false);
    }
  };

  // Handle download visual inspection report
  const handleDownloadVisualInspectionReport = async (weldData: any) => {
    // The correct path is productionWeldVisualInspectionId
    const inspectionId = weldData?.productionWeldVisualInspectionId;
    setIsReportLoading(true);
    // Validate required data
    if (!inspectionId) {
      Notification.error('Visual Inspection ID not found');
      return;
    }

    // const payload = {
    //   productionWeldInspectionId: inspectionId
    // };

    // // Dispatch action to set loading state
    // dispatch(downloadVisualInspectionReport(payload));

    // Call API directly and handle download in .then()
    try {
      const params = new URLSearchParams({
        productionWeldInspectionId: inspectionId.toString()
      });

      const url = `/download-proxy/reports/visualInspection?${params.toString()}`;
      const res = await axiosConfig.get(url);

      if (res.status === 200 && res.data.success === 1 && res.data.data?.downloadUrl) {
        // Download the PDF by opening in new tab
        loadProductionData();
      } else {
        Notification.error('Failed to generate report');
      }
    } catch (error) {
      Notification.error('Error downloading report');
    } finally {
      // Clear download state
      dispatch(clearVisualInspectionReportDownload());
      setIsReportLoading(false);
    }
  };

  // Handle download alignment report
  const handleDownloadAlignmentReport = async () => {
    // Validate required data
    if (!selectedItem?.projectId || !selectedPipe?.projectId) {
      Notification.error('Project ID not found');
      return;
    }
    if (!selectedItem?.pipeId || !selectedPipe?.pipeId) {
      Notification.error('Pipe ID not found');
      return;
    }
    if (!alignmentWeldData?.productionId && !productionSheetData?.productionId) {
      Notification.error('Production ID not found');
      return;
    }
    setIsReportLoading(true);
    const payload = {
      projectId: selectedItem.projectId || selectedPipe.projectId,
      pipeId: selectedItem.pipeId || selectedPipe.pipeId,
      productionId: alignmentWeldData?.productionId || productionSheetData?.productionId,
      productionWeldAlignmentId: alignmentWeldData?.productionWeldAlignmentId || productionSheetData?.productionWeldAlignmentId
    };

    // // Dispatch action to set loading state
    // dispatch(downloadAlignmentReport(payload));

    // Call API directly and handle download in .then()
    try {
      const params = new URLSearchParams({
        projectId: payload.projectId.toString(),
        pipeId: payload.pipeId.toString(),
        productionId: payload.productionId.toString(),
        productionWeldAlignmentId: payload.productionWeldAlignmentId.toString()
      });

      const url = `/download-proxy/reports/alignment-verification?${params.toString()}`;
      const res = await axiosConfig.get(url);

      if (res.status === 200 && res.data.success === 1 && res.data.data?.downloadUrl) {
        loadProductionData();
      } else {
        Notification.error('Failed to generate report');
      }
    } catch (error) {
      Notification.error('Error downloading report');
    } finally {
      // Clear download state
      dispatch(clearAlignmentReportDownload());
      setIsReportLoading(false);
    }
  };




  // Clear forge search term when select closes
  useEffect(() => {
    if (!isForgeSelectOpen) {
      setForgeSearchTerm('');
    }
  }, [isForgeSelectOpen]);

  // Set initial edit mode based on signatures when view or data changes
  useEffect(() => {
    if (currentView === 'fitment') {
      const signaturesExist = fitmentWeldData?.signature?.qc?.signature && fitmentWeldData?.signature?.customer?.signature;
      setIsEditMode(!signaturesExist);
    } else if (currentView === 'visual') {
      const signaturesExist = visualInspectionWeldData?.signature?.qc?.signature && visualInspectionWeldData?.signature?.customer?.signature;
      setIsEditMode(!signaturesExist);
    } else if (currentView === 'alignment') {
      const signaturesExist = alignmentWeldData?.signature?.qc?.signature && alignmentWeldData?.signature?.customer?.signature;
      setIsEditMode(!signaturesExist);
    }
  }, [currentView, fitmentWeldData, visualInspectionWeldData, alignmentWeldData]);





  // Define the form data type
  interface FormDataToUse {
    startDate: string;
    endDate: string;
    productionStatus: string;
    wpsDocId: number | null;
    drawingDocId: number | null;
    materialGasIds: number[];
    materialForgeId: number | null;
    materialWireLotIds: number[];
    materialPipeId: number | null;
    distanceFromPipeEndToForge: number | null;
    productionWelds: Array<{
      weldPasses?: any[];
    }>;
    passConfigDocId: number | null;
  }

  // Default form data when no production data exists
  const getDefaultFormData = (): FormDataToUse => ({
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    productionStatus: 'In Progress',
    wpsDocId: null,
    drawingDocId: null,
    materialGasIds: [],
    materialForgeId: null,
    materialWireLotIds: [],
    materialPipeId: null,
    distanceFromPipeEndToForge: null,
    productionWelds: [],
    passConfigDocId: null

  });
  const loadProductionData = async () => {
    // Use actual project ID from props instead of selectedItem
    const projectId = project?.id || project?.projectId || project?.project?.projectId;
    const pipeId = selectedPipe?.id || selectedPipe?.pipeId;
    const productionId = selectedItem?.productionId;

    if (projectId && pipeId && productionId) {
      try {
        setIsLoadingData(true);
        await dispatch(fetchProductionData({
          projectId: projectId,
          pipe_id: pipeId,
          production_id: productionId
        }));
      } catch (error) {
        Notification.error('Failed to load production data');
      } finally {
        setIsLoadingData(false);
      }
    }
  };
  // Load production data when selectedItem changes
  useEffect(() => {
    // Reset the processed data ref when selectedItem changes to ensure new data is processed
    lastProcessedDataRef.current = null;
    //console.log('selectedItem', selectedItem);

    // Clear previous production data to prevent loading stale data
    // dispatch(clearProductionData());

    // Load new production data for the selected item
    loadProductionData();
  }, [selectedItem]);

  // // Watch for changes in selectedPassConfigurationId and update data accordingly
  // useEffect(() => {
  //   if (selectedPassConfigurationId && specifications && specifications.length > 0) {
  //     const selectedPassConfig = specifications.find(spec =>
  //       spec.id.toString() === selectedPassConfigurationId && spec.type === 'PASS_CONFIG'
  //     );

  //     if (selectedPassConfig && selectedPassConfig.files.length > 0 && selectedPassConfig.files[0].details?.passConfig?.passes) {
  //       const passConfig = selectedPassConfig.files[0].details.passConfig.passes;

  //       // Create pass configuration data for both welds using validation function
  //       const passConfigData = validateAndFormatPassConfig(passConfig);
  //       const passConfigData2 = validateAndFormatPassConfig(passConfig);
  //       // Update both weld pass configurations
  //       setPassConfigurationWeld_1([...passConfigData]);
  //       setPassConfigurationWeld_2([...passConfigData2]);

  //       // Set timestamp for when configuration was updated
  //       setPassConfigurationLastUpdated(new Date());
  //     }
  //   }
  // }, [specifications]);

  // Load team members when selectedItem changes
  useEffect(() => {
    // Use actual project ID from props instead of selectedItem
    const projectId = project?.id || project?.projectId || project?.project?.projectId;
    if (projectId) {
      loadTeamMembers(projectId);
    }
    loadtetAllTeamMembers();
  }, [project, loadTeamMembers]);




  // Handle production data loading and error states
  useEffect(() => {
    if (productionDataError) {
      Notification.error(productionDataError || 'Failed to load production data');
    }
  }, [productionDataError]);

  // Handle team members loading and error states
  useEffect(() => {
    if (teamMembersError) {
      Notification.error(teamMembersError || 'Failed to load team members');
    }
  }, [teamMembersError]);
  const loadtetAllTeamMembers = async () => {
    //console.log('teamMembers', teamMembers);
    if (!teamMembersLoading && teamMembers && teamMembers.length > 0) {


      // Filter team members by role and create dropdown options
      const welders = teamMembers
        .filter((member: any) => member?.team_member_role === 'Welder')
        .map((member: any) => ({
          value: member.team_member_id.toString(),
          name: `${member.user.first_name} ${member.user.last_name}`,
          label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`,
          userId: member?.user_id
        }));

      const qcInspectors = teamMembers
        .filter((member: any) => member?.team_member_role === 'Quality Control' || member?.team_member_role === 'QC' || member?.team_member_role === 'QA/QC')
        .map((member: any) => ({
          value: member.team_member_id.toString(),
          name: `${member.user.first_name} ${member.user.last_name}`,
          label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`,
          userId: member?.user_id
        }));

      // const managers = teamMembers
      //   .filter((member: any) => member.projectLevelRole?.project_role_name === 'Manager' || member.projectLevelRole?.project_role_name === 'Project Manager')
      //   .map((member: any) => ({
      //     value: member.team_member_id.toString(),
      //     label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`
      //   }));

      // const supervisors = teamMembers
      //   .filter((member: any) => member.projectLevelRole?.project_role_name === 'Supervisor' || member.projectLevelRole?.project_role_name === 'Project Supervisor')
      //   .map((member: any) => ({
      //     value: member.team_member_id.toString(),
      //     label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`
      //   }));

      const customerReps = teamMembers
        .filter((member: any) => member?.team_member_role === 'Customer Representative' || member?.team_member_role === 'Client Representative' && member?.user_id === currentUserTeamMemberId)
        .map((member: any) => ({
          value: member.team_member_id.toString(),
          name: `${member.user.first_name} ${member.user.last_name}`,
          label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`,
          userId: member?.user_id
        }));

      // // Debug: Check if isWelderUser prop is coming through
      // console.log('isWelderUser prop value:', isWelderUser);
      // console.log('welders.length:', welders);
      // console.log('currentUserTeamMemberId:', currentUserTeamMemberId);

      if (isAdminOrSuperAdmin || isQCInspectorUser || isCustomerRepresentativeUser) {
        setWelderList(welders);
      }
      else if (isWelderUser && welders.length > 0) {
        setWelderList(welders.filter((member: any) => member.userId === currentUserTeamMemberId));
      }
      else {
        setWelderList([]);
      }
      // Update state with filtered lists
      // setWelderList(welders);
      if (isAdminOrSuperAdmin) {
        setQcList(qcInspectors);
      }
      else if (isQCInspectorUser && qcInspectors.length > 0) {
        setQcList(qcInspectors.filter((member: any) => member.userId === currentUserTeamMemberId));
      }
      else {
        setQcList([]);
      }

      if (isAdminOrSuperAdmin) {
        setCustomerRepresentativeList(customerReps);
      }
      else if (isCustomerRepresentativeUser && customerReps.length > 0) {
        setCustomerRepresentativeList(customerReps.filter((member: any) => member.userId === currentUserTeamMemberId));
      }
      else {
        setCustomerRepresentativeList([]);
      }



      //setCustomerRepresentativeList(customerReps);

      //     welders: welders.length,
      //     qcInspectors: qcInspectors.length,
      //  //   managers: managers.length,
      //    // supervisors: supervisors.length,
      //     customerReps: customerReps.length
      //   });
    }
  }

  // Log team members when they change
  useEffect(() => {
    loadtetAllTeamMembers();
  }, [teamMembers, teamMembersLoading]);

  // Restore saved selections when production data loads
  useEffect(() => {
    if (productionSheetData) {
      // Restore welder selections
      if (productionSheetData.productionWelds && productionSheetData.productionWelds.length > 0) {
        const weld1 = productionSheetData.productionWelds[0];
        const weld2 = productionSheetData.productionWelds.length > 1 ? productionSheetData.productionWelds[1] : null;

        // Restore welder 1: prefer weldId (array as JSON string), fallback to welderId (single value)
        if (weld1?.weldId) {
          try {
            // Parse JSON string array
            const parsedWeldId = JSON.parse(weld1.weldId);
            if (Array.isArray(parsedWeldId) && parsedWeldId.length > 0) {
              setSelectedWelder1(parsedWeldId.map((id: any) => String(id)));
            } else if (parsedWeldId) {
              setSelectedWelder1([String(parsedWeldId)]);
            } else {
              setSelectedWelder1([]);
            }
          } catch (e) {
            // If parsing fails, treat as single string value
            setSelectedWelder1([String(weld1.weldId)]);
          }
        } else if (weld1?.welderId) {
          // Fallback to welderId (single value) for backward compatibility
          if (Array.isArray(weld1.welderId)) {
            setSelectedWelder1(weld1.welderId.map((id: any) => id.toString()));
          } else {
            setSelectedWelder1([weld1.welderId.toString()]);
          }
        } else {
          // Clear selection if no welder data
          setSelectedWelder1([]);
        }

        // Restore welder 2: prefer weldId (array as JSON string), fallback to welderId (single value)
        if (weld2?.weldId) {
          try {
            // Parse JSON string array
            const parsedWeldId = JSON.parse(weld2.weldId);
            if (Array.isArray(parsedWeldId) && parsedWeldId.length > 0) {
              setSelectedWelder2(parsedWeldId.map((id: any) => String(id)));
            } else if (parsedWeldId) {
              setSelectedWelder2([String(parsedWeldId)]);
            } else {
              setSelectedWelder2([]);
            }
          } catch (e) {
            // If parsing fails, treat as single string value
            setSelectedWelder2([String(weld2.weldId)]);
          }
        } else if (weld2?.welderId) {
          // Fallback to welderId (single value) for backward compatibility
          if (Array.isArray(weld2.welderId)) {
            setSelectedWelder2(weld2.welderId.map((id: any) => id.toString()));
          } else {
            setSelectedWelder2([weld2.welderId.toString()]);
          }
        } else {
          // Clear selection if no welder data
          setSelectedWelder2([]);
        }

        // Restore fitment state
        if (weld1?.weldFitment) {
          setFitmentState(weld1.weldFitment);
        }
        if (weld1?.weldVisualInspection) {
          setVisualInspectionState(weld1.weldVisualInspection);
        }
        // if (weld1?.weldAlignment) {
        //   setAlignmentState(weld1.weldAlignment);
        // }

        // Restore customer representative and QC inspector
        if (productionSheetData.customerRepId) {
          setSelectedCustomerRepresentativeId(productionSheetData.customerRepId.toString());
        }
        if (productionSheetData.qcInspectorId) {
          setSelectedQcInspectorId(productionSheetData.qcInspectorId.toString());
        }
      }

      // Check if signatures exist - if not, default to editable mode
      const signaturesExist = productionSheetData?.signature?.qc?.signature && productionSheetData?.signature?.customer?.signature;
      // console.log('signaturesExist', signaturesExist);
      setIsProductionSheetEditMode(!signaturesExist);
    }
    else {
      // If no data, default to editable mode (no signatures = editable)
      setIsProductionSheetEditMode(true);
    }
  }, [productionSheetData]);

  // Initialize form with production data or defaults
  useEffect(() => {
    if (!productionDataLoading && !isLoadingData && productionDataSuccess && productionData && productionData.length > 0) {
      // Verify that the production data matches the currently selected item
      const currentProductionId = selectedItem?.productionId;
      const existingData = productionData[0] as any;

      // Skip processing if the data doesn't match the current selected item
      // Check if productionId exists in the data and compare it
      if (currentProductionId && existingData?.productionId && existingData.productionId.toString() !== currentProductionId.toString()) {
        console.log('Skipping processing: production data does not match selected item', {
          dataProductionId: existingData.productionId,
          selectedProductionId: currentProductionId
        });
        return;
      }

      // Create a unique identifier for the current data to prevent duplicate processing
      // const currentDataId = JSON.stringify(productionData);

      // // Skip processing if we've already processed this exact data
      // if (lastProcessedDataRef.current === currentDataId) {
      //   return;
      // }

      let formDataToUse = getDefaultFormData();
      // If production data exists and is not empty, use it
      if (productionData && productionData.length > 0) {
        // existingData already extracted above
        const productionSheet = existingData.productionSheet;

        if (productionSheet) {
          setProductionSheetData(productionSheet);
          formDataToUse = {
            startDate: productionSheet.startDate || dayjs().format('YYYY-MM-DD'),
            endDate: productionSheet.endDate || dayjs().format('YYYY-MM-DD'),
            productionStatus: existingData?.productionStatus,
            wpsDocId: productionSheet?.wpsDoc?.docId || null,
            drawingDocId: productionSheet?.drawingDoc?.docId || null,
            materialGasIds: productionSheet.materialGasIds || [],
            materialForgeId: productionSheet.materialForgeId || null,
            materialWireLotIds: productionSheet.materialWireLotIds || [],
            materialPipeId: productionSheet.materialPipeId || null,
            distanceFromPipeEndToForge: convertSetupValue(productionSheet?.distanceFromPipeEndToForge) || null,
            productionWelds: productionSheet.productionWelds || [],
            passConfigDocId: productionSheet?.passConfigDoc?.docId || null

          };
        }
        else {
          setProductionSheetData(null);
        }
      }

      // Update form state with the data
      setFormData({
        startDate: formDataToUse.startDate || dayjs().format('YYYY-MM-DD'),
        endDate: formDataToUse.endDate || dayjs().format('YYYY-MM-DD')
      });
      if (formDataToUse.productionStatus == 'not_started') {
        setProductionStatus('In Progress');
      }
      else {
        setProductionStatus(formDataToUse.productionStatus || selectedItem?.productionStatus || 'In Progress');
      }
      setSelectedWpsId(formDataToUse.wpsDocId ? formDataToUse.wpsDocId.toString() : null);
      setSelectedDrawingId(formDataToUse.drawingDocId ? formDataToUse.drawingDocId.toString() : null);

      const validGasIds = formDataToUse.materialGasIds
        .map((id: number) => id.toString())
        .filter((id: string) => gasOptions.some(option => option.value === id));

      setSelectedGasIds(validGasIds);



      // Filter wire IDs to only include those that exist in wireLotOptions
      const validWireIds = formDataToUse.materialWireLotIds
        .map((id: number) => id.toString())
        .filter((id: string) => wireLotOptions.some(option => option.value === id));

      setSelectedWireIds(validWireIds);


      // Fix for Pipe restoration - only set if pipe options are available
      const pipeIdToSet = formDataToUse.materialPipeId ? formDataToUse.materialPipeId.toString() : null;
      setSelectedPipeId(pipeIdToSet);

      setSelectedForgeId(formDataToUse.materialForgeId ? formDataToUse.materialForgeId.toString() : null);
      setDistanceFromPipeEnd(formDataToUse.distanceFromPipeEndToForge);

      setSelectedPassConfigurationId(formDataToUse.passConfigDocId ? formDataToUse.passConfigDocId.toString() : null);
      if (formDataToUse.passConfigDocId) {
        setIsPassConfigurationSaved(true);
      }
      else {
        setIsPassConfigurationSaved(false);
      }
      // Initialize pass configurations if production welds exist
      if (formDataToUse.productionWelds && formDataToUse.productionWelds.length > 0) {
        const weld1 = formDataToUse.productionWelds[0] as any;
        const weld2 = formDataToUse.productionWelds.length > 1 ? formDataToUse.productionWelds[1] as any : null;

        setweld1Name(weld1.weldName || null);
        setweld2Name(weld2.weldName || null);

        // Load per-weld pipe NRI and pass config from productionWeldExtraDetails (or fallback to top-level)
        const parseExtraDetails = (weld: any) => {
          const raw = weld?.productionWeldExtraDetails;
          if (raw == null) return null;
          if (typeof raw === 'string') {
            try { return JSON.parse(raw); } catch { return null; }
          }
          return typeof raw === 'object' ? raw : null;
        };
        const extra1 = parseExtraDetails(weld1);
        const extra2 = parseExtraDetails(weld2);
        if (extra1?.materialPipeId != null) setWeld1PipeId(String(extra1.materialPipeId));
        else if (weld1?.materialPipeId != null) setWeld1PipeId(String(weld1.materialPipeId));
        else setWeld1PipeId(null);
        if (extra2?.materialPipeId != null) setWeld2PipeId(String(extra2.materialPipeId));
        else if (weld2?.materialPipeId != null) setWeld2PipeId(String(weld2.materialPipeId));
        else setWeld2PipeId(null);
        if (extra1?.passConfigDocId != null) setWeld1PassConfigId(String(extra1.passConfigDocId));
        else if (weld1?.passConfigDocId != null) setWeld1PassConfigId(String(weld1.passConfigDocId));
        else if (passConfigurationOptions.length > 0) setWeld1PassConfigId(String(passConfigurationOptions[0].value));
        else setWeld1PassConfigId(null);
        if (extra2?.passConfigDocId != null) setWeld2PassConfigId(String(extra2.passConfigDocId));
        else if (weld2?.passConfigDocId != null) setWeld2PassConfigId(String(weld2.passConfigDocId));
        else if (passConfigurationOptions.length > 0) setWeld2PassConfigId(String(passConfigurationOptions[0].value));
        else setWeld2PassConfigId(null);
        if (extra1?.fitmentSpecDocId != null) setWeld1FitmentId(String(extra1.fitmentSpecDocId));
        else if (weld1?.fitmentSpecDocId != null) setWeld1FitmentId(String(weld1.fitmentSpecDocId));
        else if (fitmentOptions.length > 0) setWeld1FitmentId(String(fitmentOptions[0].value));
        else setWeld1FitmentId(null);
        if (extra2?.fitmentSpecDocId != null) setWeld2FitmentId(String(extra2.fitmentSpecDocId));
        else if (weld2?.fitmentSpecDocId != null) setWeld2FitmentId(String(weld2.fitmentSpecDocId));
        else if (fitmentOptions.length > 0) setWeld2FitmentId(String(fitmentOptions[0].value));
        else setWeld2FitmentId(null);
        UpdatePassConfiguration(
          weld1.weldPasses,
          weld2?.weldPasses || null,
          weld1,
          weld2,
          extra1?.passConfigDocId ?? weld1?.passConfigDocId ?? formDataToUse.passConfigDocId ?? undefined,
          extra2?.passConfigDocId ?? weld2?.passConfigDocId ?? formDataToUse.passConfigDocId ?? undefined
        );
        if (productionSheetData?.productionAlignment) {
          handleCompletionStatusChange('alignment');
        }
        if (weld1?.weldFitment) {
          handleCompletionStatusChange('fitment');
        }
        if (weld1?.weldVisualInspection) {
          handleCompletionStatusChange('visualInspection');
        }

        if (weld2?.weldFitment) {
          handleCompletionStatusChange('fitment');
        }
        if (weld2?.weldVisualInspection) {
          handleCompletionStatusChange('visualInspection');
        }

        // console.log('productionSheetData?.productionRepairAlignments', productionSheetData?.productionRepairAlignments);
        // Load repair alignments from productionSheetData
        if (productionSheetData?.productionRepairAlignments && Array.isArray(productionSheetData.productionRepairAlignments)) {
          setRepairAlignments(productionSheetData.productionRepairAlignments);
        } else {
          setRepairAlignments([]);
        }

        // Load repair welds
        type RepairWeldEntry = { weldName: string, passConfiguration: any[], materialPipeId?: string, passConfigDocId?: string, fitmentSpecDocId?: string, productionWeldId?: number | null };
        const repairWeldsMap = new Map<string, RepairWeldEntry>();
        let maxRepairWeld1 = 0;
        let maxRepairWeld2 = 0;

        // Get base pass configurations for initialization
        const basePassConfig1 = weld1?.weldPasses || [];
        const basePassConfig2 = weld2?.weldPasses || [];

        // Process all welds to find repairs
        formDataToUse.productionWelds.forEach((weld: any) => {
          // Check for repairindex (new format) or repaire (backward compatibility)
          const repairindex = weld.repairindex !== undefined && weld.repairindex !== null ? weld.repairindex :
            (weld.repaire ? Number(weld.repaire) : null);

          if (repairindex !== null && repairindex !== undefined && repairindex > 0) {
            // This is a repair weld
            // Support both new format (weldindex) and old format (weldId) for backward compatibility
            const weldindex = weld.weldindex !== undefined && weld.weldindex !== null ? weld.weldindex :
              (weld.weldId === '1' || weld.weldId === 1 ? 1 :
                weld.weldId === '2' || weld.weldId === 2 ? 2 : 1);

            const key = getRepairWeldKey(weldindex, repairindex);
            const repairExtra = parseExtraDetails(weld);

            // Use saved pass configuration or initialize from base
            let passConfig = weld.weldPasses || [];
            if (passConfig.length === 0) {
              const baseConfig = weldindex === 1 ? basePassConfig1 : basePassConfig2;
              passConfig = baseConfig.map((pass: any) => ({
                ...pass,
                passStatus: false,
                passTemperature: null,
                passWelder: []
              }));
            }

            repairWeldsMap.set(key, {
              weldName: weld.weldName || '',
              passConfiguration: passConfig,
              materialPipeId: repairExtra?.materialPipeId != null ? String(repairExtra.materialPipeId) : undefined,
              passConfigDocId: repairExtra?.passConfigDocId != null ? String(repairExtra.passConfigDocId) : undefined,
              fitmentSpecDocId: repairExtra?.fitmentSpecDocId != null ? String(repairExtra.fitmentSpecDocId) : undefined,
              productionWeldId: weld.productionWeldId ?? null,
            });

            // Track max repair numbers
            if (weldindex === 1) {
              maxRepairWeld1 = Math.max(maxRepairWeld1, repairindex);
            } else if (weldindex === 2) {
              maxRepairWeld2 = Math.max(maxRepairWeld2, repairindex);
            }
          }
        });

        setRepairWelds(repairWeldsMap);
        setNumberOfRepairsWeld1(maxRepairWeld1);
        setNumberOfRepairsWeld2(maxRepairWeld2);

        // If production welds exist, pass configuration is already saved
        //  setIsPassConfigurationSaved(true);
        // setSelectedPassConfigurationId(null);
      }
      else if (specifications && specifications.length > 0) {
        UpdatePassConfiguration(passConfigurationWeld_1, passConfigurationWeld_2);

      }

      // Mark this data as processed to prevent duplicate processing
      //lastProcessedDataRef.current = currentDataId;
    }
  }, [productionData, productionDataSuccess]);


  // Helper function to extract welder IDs from weldId (for backward compatibility)
  const extractWelderIdsFromWeldId = (weldId: any): string[] => {
    if (!weldId) return [];
    try {
      // Try parsing as JSON string array
      const parsed = JSON.parse(weldId);
      if (Array.isArray(parsed)) {
        return parsed.map((id: any) => String(id));
      }
      return [String(weldId)];
    } catch (e) {
      // If parsing fails, treat as single string value
      return [String(weldId)];
    }
  };

  const UpdatePassConfiguration = (
    passConfigurationWeld_1: any,
    passConfigurationWeld_2: any,
    weld1?: any,
    weld2?: any,
    preferredPassConfigDocIdWeld1?: number | null,
    preferredPassConfigDocIdWeld2?: number | null
  ) => {
    const passConfiguration = specifications.filter(spec => spec.type === 'PASS_CONFIG');
    if (passConfiguration && passConfiguration.length > 0) {
      const sortedByNewest = [...passConfiguration].sort((a: any, b: any) => b.id - a.id);
      // Resolve spec per weld: use productionWeldExtraDetails.passConfigDocId only if it matches a spec; else use first (latest)
      const resolveSpec = (preferredId: number | null | undefined) => {
        if (preferredId != null && preferredId > 0) {
          const found = passConfiguration.find((s: any) => s.id === preferredId || Number(s.id) === preferredId);
          if (found) return found;
        }
        return sortedByNewest[0];
      };
      const specWeld1 = resolveSpec(preferredPassConfigDocIdWeld1);
      const specWeld2 = resolveSpec(preferredPassConfigDocIdWeld2);
      setSelectedPassConfigurationId(specWeld1.id.toString());
      setWeld1PassConfigId(specWeld1.id.toString());
      setWeld2PassConfigId(specWeld2.id.toString());

      const passes1 = specWeld1.files?.length > 0 && specWeld1.files[0].details?.passConfig?.passes;
      const passes2 = specWeld2.files?.length > 0 && specWeld2.files[0].details?.passConfig?.passes;
      if (passes1 && passes2) {
        const formattedSpecPasses1 = validateAndFormatPassConfig(passes1);
        const formattedSpecPasses2 = validateAndFormatPassConfig(passes2);
        // Merge each weld with its own selected spec (from productionWeldExtraDetails or first)
        let updatedWeld1 = compareAndUpdatePassConfiguration(formattedSpecPasses1, passConfigurationWeld_1);
        let updatedWeld2 = compareAndUpdatePassConfiguration(formattedSpecPasses2, passConfigurationWeld_2);

        // Backward compatibility: If passes don't have passWelder but weldId exists, populate all passes
        if (weld1?.weldId) {
          const welderIdsFromWeldId = extractWelderIdsFromWeldId(weld1.weldId);
          // Check if any pass has passWelder (new format)
          const hasPassWelder = updatedWeld1.some((pass: any) => pass.passWelder && Array.isArray(pass.passWelder) && pass.passWelder.length > 0);

          if (!hasPassWelder && welderIdsFromWeldId.length > 0) {
            // Populate all passes with welder IDs from weldId
            updatedWeld1 = updatedWeld1.map((pass: any) => ({
              ...pass,
              passWelder: [...welderIdsFromWeldId]
            }));
            // Also update selectedWelder1 for backward compatibility
            //  setSelectedWelder1(welderIdsFromWeldId);
          } else if (hasPassWelder) {
            // New format: extract welder IDs from passes for selectedWelder1
            const allWelderIds = new Set<string>();
            updatedWeld1.forEach((pass: any) => {
              if (Array.isArray(pass.passWelder)) {
                pass.passWelder.forEach((id: string) => allWelderIds.add(id));
              }
            });
            //  setSelectedWelder1(Array.from(allWelderIds));
          }
        }

        if (weld2?.weldId) {
          const welderIdsFromWeldId = extractWelderIdsFromWeldId(weld2.weldId);
          // Check if any pass has passWelder (new format)
          const hasPassWelder = updatedWeld2.some((pass: any) => pass.passWelder && Array.isArray(pass.passWelder) && pass.passWelder.length > 0);

          if (!hasPassWelder && welderIdsFromWeldId.length > 0) {
            // Populate all passes with welder IDs from weldId
            updatedWeld2 = updatedWeld2.map((pass: any) => ({
              ...pass,
              passWelder: [...welderIdsFromWeldId]
            }));
            // Also update selectedWelder2 for backward compatibility
            //  setSelectedWelder2(welderIdsFromWeldId);
          } else if (hasPassWelder) {
            // New format: extract welder IDs from passes for selectedWelder2
            const allWelderIds = new Set<string>();
            updatedWeld2.forEach((pass: any) => {
              if (Array.isArray(pass.passWelder)) {
                pass.passWelder.forEach((id: string) => allWelderIds.add(id));
              }
            });
            // setSelectedWelder2(Array.from(allWelderIds));
          }
        }

        setPassConfigurationWeld_1(updatedWeld1);
        setPassConfigurationWeld_2(updatedWeld2);
      }
    }
  }



  // State for tracking completion status
  const [completionStatus, setCompletionStatus] = useState({
    fitment: false,
    visualInspection: false,
    alignment: false
  });

  // State for WPS and Drawing selections
  const [selectedWpsId, setSelectedWpsId] = useState<string | null>(null);
  const [selectedDrawingId, setSelectedDrawingId] = useState<string | null>(null);

  // State for production status
  const [productionStatus, setProductionStatus] = useState<string | null>(null);

  // Form data state for dates
  const [formData, setFormData] = useState({
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD')
  });

  const getMaterials = (type: string) => {
    if (!materials) return [];

    switch (type) {
      case 'pipe':
        return materials.pipes || [];
      case 'forge':
        return materials.forge || [];
      case 'gas':
        return materials.gas || [];
      case 'wire':
        return materials.wireLot || [];
      case 'coating':
        return materials.coating || [];
      default:
        return [];
    }
  };

  // Create options for gas and wire lot dropdowns
  const gasOptions = getMaterials('gas')
    .map((gas: any) => ({
      value: gas.materialId?.toString() || gas.id?.toString() || gas.materialId,
      gasSerialNumber: gas.details?.gasSerialNumber,
      label: gas.details?.gasSerialNumber || gas.gasSerialNumber || `Gas ${gas.materialId || gas.id}`,
      status: gas.materialStatus
    }));

  const wireLotOptions = getMaterials('wire')
    .map((wire: any) => ({
      value: wire.materialId?.toString() || wire.id?.toString() || wire.materialId,
      wireLotNumber: wire.details?.wireLotNumber,
      label: wire.details?.wireLotNumber || wire.lotNumber || `Wire Lot ${wire.materialId || wire.id}`,
      status: wire.materialStatus
    }));


  // Create options for forge and pipe dropdowns
  const forgeOptions = getMaterials('forge')
    .map((forge: any) => ({
      value: forge.materialId.toString(),
      forgeNRI: forge.details?.forgeNRI,
      label: `${forge.details?.forgeNRI} (${forge.materialStatus})` || `Forge ${forge.materialId}`,
      status: forge.materialStatus
    }));

  const pipeOptions = getMaterials('pipe')
    .map((pipe: any) => ({
      value: pipe.materialId.toString(),
      pipeNRI: pipe.details?.pipeNRI,
      label: `${pipe.details?.pipeNRI} (${pipe.materialStatus})` || `Pipe ${pipe.materialId}`,
      status: pipe.materialStatus
    }));

  // Create options for specifications dropdowns
  const wpsOptions = specifications
    .filter(spec => spec.type === 'WPS')
    .map(wps => ({
      value: wps.id,
      label: wps.files?.[wps.files.length - 1]?.fileName || `WPS ${wps.id}`
    }));



  const drawingOptions = specifications
    .filter(spec => spec.type === 'DRAWING')
    .map(drawing => ({
      value: drawing.id,
      label: drawing.files?.[drawing.files.length - 1]?.fileName || `Drawing ${drawing.id}`
    }));




  const passConfigurationOptions = specifications
    .filter(spec => spec.type === 'PASS_CONFIG')
    .map((pass, index: number) => ({
      value: pass.id,
      passConfig: pass.files?.[0]?.details?.passConfig,
      label: `Pass Configuration ${index + 1}`
    }));

  //console.log('passConfigurationOptions', passConfigurationOptions);
  // Specification-based options for Fitment (FITMENT_READINGS) and Visual Inspection (INSPECTION_PROCEDURE)
  const fitmentOptions = specifications
    .filter((spec: any) => spec.type === 'FITMENT_READINGS')
    .map((spec: any, index: number) => ({
      value: String(spec.id),
      label: `Fitment ${index + 1}`
    }));

  // console.log('fitmentOptions', fitmentOptions);

  // Initialize Weld Section dialog pending state when dialog opens (Weld 1/2 or repair)
  useEffect(() => {
    if (weldSectionDialogWeldIndex == null) return;
    const repairIndex = weldSectionDialogRepairIndex ?? 0;
    if (repairIndex === 0) {
      if (weldSectionDialogWeldIndex === 1) {
        setWeldSectionPendingPipeId(weld1PipeId);
        setWeldSectionPendingPassConfigId(weld1PassConfigId ?? (passConfigurationOptions.length > 0 ? String(passConfigurationOptions[0].value) : null));
        setWeldSectionPendingFitmentId(weld1FitmentId ?? (fitmentOptions.length > 0 ? String(fitmentOptions[0].value) : null));
      } else if (weldSectionDialogWeldIndex === 2) {
        setWeldSectionPendingPipeId(weld2PipeId);
        setWeldSectionPendingPassConfigId(weld2PassConfigId ?? (passConfigurationOptions.length > 0 ? String(passConfigurationOptions[0].value) : null));
        setWeldSectionPendingFitmentId(weld2FitmentId ?? (fitmentOptions.length > 0 ? String(fitmentOptions[0].value) : null));
      }
    } else {
      const key = getRepairWeldKey(weldSectionDialogWeldIndex, repairIndex);
      const repairData = repairWelds.get(key);
      setWeldSectionPendingPipeId(repairData?.materialPipeId ?? null);
      setWeldSectionPendingPassConfigId(repairData?.passConfigDocId ?? (passConfigurationOptions.length > 0 ? String(passConfigurationOptions[0].value) : null));
      setWeldSectionPendingFitmentId(repairData?.fitmentSpecDocId ?? (fitmentOptions.length > 0 ? String(fitmentOptions[0].value) : null));
    }
  }, [weldSectionDialogWeldIndex, weldSectionDialogRepairIndex, repairWelds]);

  useEffect(() => {
    setSelectedHeadings((prev: any) => ({
      ...prev,
      wps: wpsOptions.find(option => option.value === selectedWpsId) || null,
      drawing: drawingOptions.find(option => option.value === selectedDrawingId) || null,
      passConfiguration: passConfigurationOptions.find(option => option.value === selectedPassConfigurationId) || null,
      forge: forgeOptions.find(option => option.value === selectedForgeId) || null,
      pipe: pipeOptions.find(option => option.value === selectedPipeId) || null,
      wire: selectedWireIds.length > 0 ? selectedWireIds.map((id: any) => wireLotOptions.find(option => option.value === id)) : null,
      gas: selectedGasIds.length > 0 ? selectedGasIds.map((id: any) => gasOptions.find(option => option.value === id)) : null
    }));

  }, [selectedWpsId, selectedDrawingId, selectedForgeId, selectedPipeId, selectedPassConfigurationId, selectedWireIds, selectedGasIds]);

  useEffect(() => {

    setSelectedHeadings((prev: any) => ({
      ...prev,
      welder1: selectedWelder1.length > 0 ? selectedWelder1.map(id => welderList.find((option: any) => option.value === id)).filter(Boolean) : null,
      welder2: selectedWelder2.length > 0 ? selectedWelder2.map(id => welderList.find((option: any) => option.value === id)).filter(Boolean) : null
    }));

  }, [selectedWelder1, selectedWelder2, welderList]); // Remove selectedWeld, add welderList

  const handleBackNavigation = () => {
    const navigationAction = () => {
      if (currentView === 'main') {
        //  dispatch(clearProductionData());
        setProductionSheetData(null);
        // setIsProductionSheetEditMode(false);
        // dispatch(setProductionData([]));


        // If in main view, exit entirely - let parent handle state cleanup
        onBack();
      } else {
        // If in sub-view (fitment/visual/alignment), go back to main
        setCurrentView('main');
      }
    };

    if (hasUnsavedChanges) {
      setPendingNavigation(() => navigationAction);
      setIsConfirmDialogOpen(true);
    } else {
      navigationAction();
    }
  };

  const handleForgeChange = (value: string) => {
    // Clear search and close select when value changes
    setForgeSearchTerm('');
    setIsForgeSelectOpen(false);

    if (value === 'none') {
      setSelectedForgeId(null);
      markAsUnsaved();
      return;
    }

    if (forgeOptions.length > 0) {
      const isValidPipeId = forgeOptions.find(option => option.value === String(value));
      if (isValidPipeId?.status === 'Consumed') {
        Notification.warning('Forge is already consumed', 'please select available Forge');
        return;
      }
    }
    setSelectedForgeId(value);
    markAsUnsaved();
  };

  const handlePipeChange = (value: string) => {
    if (value === 'none') {
      setSelectedPipeId(null);
      markAsUnsaved();
      return;
    }

    if (pipeOptions.length > 0) {
      const isValidPipeId = pipeOptions.find(option => option.value === String(value));
      if (isValidPipeId?.status === 'Consumed') {
        Notification.warning('selected Pipe is already consumed', 'please select available Pipe');
        return;
      }
    }
    setSelectedPipeId(value);
    markAsUnsaved();
  };

  const handleDistanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ? Number(e.target.value) : null;
    setDistanceFromPipeEnd(value);
    markAsUnsaved();
  };

  const handleGasChange = (value: string[]) => {
    setSelectedGasIds(value);
    markAsUnsaved();
  };

  const handleWireChange = (value: string[]) => {
    setSelectedWireIds(value);
    markAsUnsaved();
  };

  const handleQRScan = (type: 'forge' | 'pipe' | 'weld1' | 'weld2' | string, weldindex?: number, repairindex?: number) => {
    if (weldindex !== undefined && repairindex !== undefined) {
      // For repair welds, use a specific format: 'repair-weld-{weldindex}-{repairindex}'
      setScanningFor(`repair-weld-${weldindex}-${repairindex}`);
    } else {
      setScanningFor(type);
    }
    setIsQRScannerOpen(true);
  };

  const handleQRResult = (result: string) => {
    if (scanningFor === 'forge') {


      if (forgeOptions.length > 0) {
        const isValidPipeId = forgeOptions.find(option => option.forgeNRI === String(result));
        if (isValidPipeId?.status === 'Consumed') {
          Notification.warning('Forge is already consumed', 'please select available Forge');
          return;
        }
        setSelectedForgeId(isValidPipeId?.value);
        markAsUnsaved();
      }
    } else if (scanningFor === 'pipe') {
      //   const pipeArray = getMaterials('pipe') as any[];
      if (pipeOptions.length > 0) {
        const isValidPipeId = pipeOptions.find(option => option.pipeNRI === String(result));
        if (isValidPipeId?.status === 'Consumed') {
          Notification.warning('Pipe is already consumed', 'please select available Pipe');
          return;
        }
        setSelectedPipeId(isValidPipeId?.value);
        markAsUnsaved();
      }



    } else if (scanningFor === 'weld1') {
      // Update weld 1 name with scanned result
      setweld1Name(result.toUpperCase());
      markAsUnsaved();
      Notification.success('Weld 1 name updated successfully');
    } else if (scanningFor === 'weld2') {
      // Update weld 2 name with scanned result
      setweld2Name(result.toUpperCase());
      markAsUnsaved();
      Notification.success('Weld 2 name updated successfully');
    } else if (scanningFor && scanningFor.startsWith('repair-weld-')) {
      // Handle repair weld QR scan
      const parts = scanningFor.split('-');
      if (parts.length === 4) {
        const weldindex = parseInt(parts[2], 10);
        const repairindex = parseInt(parts[3], 10);
        handleRepairWeldNameChange(weldindex, repairindex, result.toUpperCase());
        Notification.success(`Repair ${repairindex} (Weld ${weldindex}) name updated successfully`);
      }
    }
    setIsQRScannerOpen(false);
    setScanningFor(null);
  };

  // Load unit from settings
  useEffect(() => {
    const loadUnit = () => {
      const unitFromSettings = getUnitFromSettings();
      setUnit(unitFromSettings);
    };
    loadUnit();
  }, []);



  const handleCompletionStatusChange = (type: 'fitment' | 'visualInspection' | 'alignment') => {
    setCompletionStatus(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleConfirmNavigation = () => {
    if (pendingNavigation) {
      pendingNavigation();
      setHasUnsavedChanges(false);
      // if (currentView === 'main') {
      //   setIsProductionSheetEditMode(false);
      // }
    }
    setIsConfirmDialogOpen(false);
    setPendingNavigation(null);
  };

  const handleCancelNavigation = () => {
    setIsConfirmDialogOpen(false);
    setPendingNavigation(null);
  };

  // Function to mark changes as unsaved
  const markAsUnsaved = () => {
    setHasUnsavedChanges(true);
  };

  const handleWpsChange = (value: string) => {
    setSelectedWpsId(value === 'none' ? null : value);
    markAsUnsaved();
  };

  const handleDrawingChange = (value: string) => {
    setSelectedDrawingId(value === 'none' ? null : value);
    markAsUnsaved();
  };

  const handlePassStatusChange_weld_1 = (checked: boolean | string, index: number) => {

    const updatedPasses = [...passConfigurationWeld_1];
    updatedPasses[index].passStatus = checked === true;
    setPassConfigurationWeld_1(updatedPasses);
    markAsUnsaved();
  };

  const handlePassTemperatureChange_weld_1 = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {

    const updatedPasses = [...passConfigurationWeld_1];
    const value = e.target.value;
    // Allow empty values or convert to number if not empty
    updatedPasses[index].passTemperature = value === '' ? null : Number(value);
    setPassConfigurationWeld_1(updatedPasses);
    markAsUnsaved();
  };

  const handlePassWelderChange_weld_1 = (selected: string[], index: number) => {
    const updatedPasses = [...passConfigurationWeld_1];
    updatedPasses[index].passWelder = selected;
    setPassConfigurationWeld_1(updatedPasses);

    // Update selectedWelder1 with all unique welder IDs from all passes for backward compatibility
    const allWelderIds = new Set<string>();
    updatedPasses.forEach((pass: any) => {
      if (Array.isArray(pass.passWelder)) {
        pass.passWelder.forEach((id: string) => allWelderIds.add(String(id)));
      }
    });
    //  setSelectedWelder1(Array.from(allWelderIds));

    markAsUnsaved();
  };

  const handlePassStatusChange_weld_2 = (checked: boolean | string, index: number) => {

    const updatedPasses = [...passConfigurationWeld_2];
    updatedPasses[index].passStatus = checked === true;
    setPassConfigurationWeld_2(updatedPasses);
    markAsUnsaved();
  };

  const handlePassTemperatureChange_weld_2 = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const updatedPasses = [...passConfigurationWeld_2];
    const value = e.target.value;
    // Allow empty values or convert to number if not empty
    updatedPasses[index].passTemperature = value === '' ? null : Number(value);
    setPassConfigurationWeld_2(updatedPasses);
    markAsUnsaved();
  };

  const handlePassWelderChange_weld_2 = (selected: string[], index: number) => {
    const updatedPasses = [...passConfigurationWeld_2];
    updatedPasses[index].passWelder = selected;
    setPassConfigurationWeld_2(updatedPasses);

    // Update selectedWelder2 with all unique welder IDs from all passes for backward compatibility
    const allWelderIds = new Set<string>();
    updatedPasses.forEach((pass: any) => {
      if (Array.isArray(pass.passWelder)) {
        pass.passWelder.forEach((id: string) => allWelderIds.add(String(id)));
      }
    });
    // setSelectedWelder2(Array.from(allWelderIds));

    markAsUnsaved();
  };

  // Handlers for repair welds
  const getRepairWeldKey = (weldindex: number, repairindex: number) => `${weldindex}-${repairindex}`;

  const handleRepairWeldNameChange = (weldindex: number, repairindex: number, value: string) => {
    const key = getRepairWeldKey(weldindex, repairindex);
    const current = repairWelds.get(key) || { weldName: '', passConfiguration: [] };
    setRepairWelds(new Map(repairWelds.set(key, { ...current, weldName: value })));
    markAsUnsaved();
  };

  const handleRepairPassStatusChange = (weldindex: number, repairindex: number, checked: boolean | string, passIndex: number) => {
    const key = getRepairWeldKey(weldindex, repairindex);
    const current = repairWelds.get(key) || { weldName: '', passConfiguration: [] };
    const updatedPasses = [...current.passConfiguration];
    if (updatedPasses[passIndex]) {
      updatedPasses[passIndex].passStatus = checked === true;
      setRepairWelds(new Map(repairWelds.set(key, { ...current, passConfiguration: updatedPasses })));
      markAsUnsaved();
    }
  };

  const handleRepairPassTemperatureChange = (weldindex: number, repairindex: number, e: React.ChangeEvent<HTMLInputElement>, passIndex: number) => {
    const key = getRepairWeldKey(weldindex, repairindex);
    const current = repairWelds.get(key) || { weldName: '', passConfiguration: [] };
    const updatedPasses = [...current.passConfiguration];
    if (updatedPasses[passIndex]) {
      const value = e.target.value;
      updatedPasses[passIndex].passTemperature = value === '' ? null : Number(value);
      setRepairWelds(new Map(repairWelds.set(key, { ...current, passConfiguration: updatedPasses })));
      markAsUnsaved();
    }
  };

  const handleRepairPassWelderChange = (weldindex: number, repairindex: number, selected: string[], passIndex: number) => {
    const key = getRepairWeldKey(weldindex, repairindex);
    const current = repairWelds.get(key) || { weldName: '', passConfiguration: [] };
    const updatedPasses = [...current.passConfiguration];
    if (updatedPasses[passIndex]) {
      updatedPasses[passIndex].passWelder = selected;
      setRepairWelds(new Map(repairWelds.set(key, { ...current, passConfiguration: updatedPasses })));
      markAsUnsaved();
    }
  };

  // Handler for common welder selection - applies to all passes and saves
  const handleRepairWelderChange = (weldindex: number, repairindex: number, selected: string[]) => {
    const key = getRepairWeldKey(weldindex, repairindex);
    const current = repairWelds.get(key) || { weldName: '', passConfiguration: [] };
    const updatedPasses = current.passConfiguration.map((pass: any) => ({
      ...pass,
      passWelder: [...selected]
    }));
    setRepairWelds(new Map(repairWelds.set(key, { ...current, passConfiguration: updatedPasses })));
    markAsUnsaved();
  };

  // Get common welder selection for repair weld (union of all pass welders) - for restore/display
  const getRepairCommonWelders = (weldindex: number, repairindex: number): string[] => {
    const key = getRepairWeldKey(weldindex, repairindex);
    const repairData = repairWelds.get(key);
    if (!repairData || !repairData.passConfiguration || repairData.passConfiguration.length === 0) {
      return [];
    }
    const allWelderIds = new Set<string>();
    repairData.passConfiguration.forEach((pass: any) => {
      if (Array.isArray(pass.passWelder)) {
        pass.passWelder.forEach((id: string) => allWelderIds.add(String(id)));
      }
    });
    return Array.from(allWelderIds);
  };

  // const handlePassConfigurationChange = (value: string) => {
  //   setSelectedPassConfigurationId(value);

  //   // Update pass configurations based on selected pass configuration
  //   if (value && specifications && specifications.length > 0) {
  //     const selectedPassConfig = specifications.find(spec =>
  //       spec.id.toString() === value && spec.type === 'PASS_CONFIG'
  //     );

  //     if (selectedPassConfig && selectedPassConfig.files.length > 0 && selectedPassConfig.files[0].details?.passConfig?.passes) {
  //       const passConfig = selectedPassConfig.files[0].details.passConfig.passes;
  //       const formattedSpecPasses = validateAndFormatPassConfig(passConfig);

  //       // Use comparison function to preserve existing saved data
  //       const updatedWeld1 = compareAndUpdatePassConfiguration(formattedSpecPasses, passConfigurationWeld_1);
  //       const updatedWeld2 = compareAndUpdatePassConfiguration(formattedSpecPasses, passConfigurationWeld_2);

  //       setPassConfigurationWeld_1(updatedWeld1);
  //       setPassConfigurationWeld_2(updatedWeld2);

  //       // Reset welder selections and other related data when pass configuration changes
  //       setSelectedWelder1(null);
  //       setSelectedWelder2(null);
  //       setSelectedCustomerRepresentativeId(null);
  //       setSelectedQcInspectorId(null);

  //       // Reset completion status
  //       setCompletionStatus({
  //         fitment: false,
  //         visualInspection: false,
  //         alignment: false
  //       });

  //       // Set timestamp for when configuration was last updated
  //       setPassConfigurationLastUpdated(new Date());

  //       // Mark as unsaved since we have new data
  //       markAsUnsaved();
  //     }
  //   } else {
  //     // If no pass configuration selected, clear the data
  //     setPassConfigurationWeld_1([]);
  //     setPassConfigurationWeld_2([]);
  //     setSelectedWelder1(null);
  //     setSelectedWelder2(null);
  //     setSelectedCustomerRepresentativeId(null);
  //     setSelectedQcInspectorId(null);
  //     setCompletionStatus({
  //       fitment: false,
  //       visualInspection: false,
  //       alignment: false
  //     });
  //     setPassConfigurationLastUpdated(null);
  //     markAsUnsaved();
  //   }
  // };

  // Add welder selection handlers
  const handleWelder1Change = (value: string[]) => {
    setSelectedWelder1(value);
    markAsUnsaved();
  };

  const handleWelder2Change = (value: string[]) => {
    setSelectedWelder2(value);
    markAsUnsaved();
  };

  const handleCustomerRepresentativeChange = (value: string) => {
    setSelectedCustomerRepresentativeId(value);
    markAsUnsaved();
  };

  const handleQcInspectorChange = (value: string) => {
    setSelectedQcInspectorId(value);
    markAsUnsaved();
  };

  const resetPassConfiguration = () => {
    setIsPassConfigurationSaved(false);
    setSelectedPassConfigurationId(null);
    setPassConfigurationWeld_1([]);
    setPassConfigurationWeld_2([]);
    setSelectedWelder1([]);
    setSelectedWelder2([]);
    setSelectedCustomerRepresentativeId(null);
    setSelectedQcInspectorId(null);

    // Reset completion status
    setCompletionStatus({
      fitment: false,
      visualInspection: false,
      alignment: false
    });

    // Clear timestamp
    setPassConfigurationLastUpdated(null);

    markAsUnsaved();
  };

  // Function to validate and format pass configuration data
  const validateAndFormatPassConfig = (passConfig: any[]) => {
    if (!Array.isArray(passConfig)) return [];

    // Create passes based on the number of passes in the configuration
    const numberOfPasses = passConfig.length;
    const passes: any[] = [];
    passConfig.forEach((pass: any) => {
      for (let i = 0; i < pass.numberOfPasses; i++) {
        passes.push({
          passName: pass.numberOfPasses > 1 ? `${pass.name} ${i + 1}` : `${pass.name}`,
          passStatus: false, // Always start with false status
          passTemperature: null, // Default temperature
          passWelder: [], // Default welder selection
          passVisualInspectionRemarks: null,
          passVisualInspectionAssessment: null
        });
      }
    });

    return passes;
  };

  // Function to compare and update pass configuration from specification
  // When saved config is missing/empty: use spec pass configuration with defaults so welds always have passes when loading
  const compareAndUpdatePassConfiguration = (specPassConfig: any[], savedPassConfig: any[]) => {
    if (!specPassConfig || !Array.isArray(specPassConfig) || specPassConfig.length === 0) return [];
    // No saved config: use spec pass configuration with defaults for both welds
    if (!savedPassConfig || !Array.isArray(savedPassConfig) || savedPassConfig.length === 0) {
      return specPassConfig.map((specPass: any) => ({
        passName: specPass.passName,
        passStatus: false,
        passTemperature: null,
        passWelder: [],
        passVisualInspectionRemarks: specPass.passVisualInspectionRemarks ?? null,
        passVisualInspectionAssessment: specPass.passVisualInspectionAssessment ?? null
      }));
    }

    // Normalize saved pass config to ensure passWelder field exists
    const normalizedSavedConfig = savedPassConfig.map((pass: any) => ({
      ...pass,
      passWelder: pass.passWelder || []
    }));

    const updatedPasses: any[] = [];

    specPassConfig.forEach((specPass: any) => {
      // Find matching saved pass by name
      const savedPass = normalizedSavedConfig.find((saved: any) => saved.passName === specPass.passName);

      if (savedPass) {
        // Update existing pass with specification data while preserving saved values
        updatedPasses.push({
          ...savedPass,
          passName: specPass.passName,
          passStatus: savedPass.passStatus,
          passTemperature: savedPass.passTemperature,
          passWelder: Array.isArray(savedPass.passWelder) ? savedPass.passWelder : [],
          passVisualInspectionRemarks: savedPass.passVisualInspectionRemarks,
          passVisualInspectionAssessment: savedPass.passVisualInspectionAssessment
        });
      } else {
        // Add new pass from specification with default values
        updatedPasses.push({
          passName: specPass.passName,
          passStatus: false,
          passTemperature: null,
          passWelder: [],
          passVisualInspectionRemarks: null,
          passVisualInspectionAssessment: null
        });
      }
    });

    return updatedPasses;
  };

  // Apply pass configuration for a single weld or repair (used from Weld Section dialog)
  const applyPassConfigurationForWeld = (weldIndex: 1 | 2, specId: string, repairIndex?: number) => {
    const spec = specifications.find((s: any) => s.type === 'PASS_CONFIG' && String(s.id) === String(specId));
    if (!spec?.files?.length || !spec.files[0].details?.passConfig?.passes) return;
    const passConfig = spec.files[0].details.passConfig.passes;
    const formattedSpecPasses = validateAndFormatPassConfig(passConfig);
    if (repairIndex != null && repairIndex > 0) {
      const key = getRepairWeldKey(weldIndex, repairIndex);
      const current = repairWelds.get(key) || { weldName: '', passConfiguration: [] };
      const merged = compareAndUpdatePassConfiguration(formattedSpecPasses, current.passConfiguration || []);
      setRepairWelds(new Map(repairWelds.set(key, { ...current, passConfiguration: merged, passConfigDocId: specId })));
    } else {
      const currentPasses = weldIndex === 1 ? passConfigurationWeld_1 : passConfigurationWeld_2;
      const merged = compareAndUpdatePassConfiguration(formattedSpecPasses, currentPasses || []);
      if (weldIndex === 1) {
        setPassConfigurationWeld_1(merged);
        setWeld1PassConfigId(specId);
      } else {
        setPassConfigurationWeld_2(merged);
        setWeld2PassConfigId(specId);
      }
    }
    markAsUnsaved();
  };

  const UpdateFitmentData = (productionWelds: any, weldIndex?: number, overrideFitmentDocId?: string | null) => {

    const fitmentData = productionWelds?.weldFitment ? { ...productionWelds.weldFitment } : {};

    fitmentData.productionId = productionSheetData?.productionId;
    fitmentData.productionWeldId = productionWelds?.productionWeldId;
    fitmentData.projectId = selectedPipe?.projectId;
    fitmentData.projectTypeId = selectedPipe?.projectTypeId;
    fitmentData.pipeId = selectedPipe?.pipeId;

    if (!fitmentData.details) {
      fitmentData.details = {};
    }
    // Use selected fitment for this weld so fitment reading matches per-weld selection (override when user just changed selection)
    const fitmentIdToUse = overrideFitmentDocId !== undefined
      ? overrideFitmentDocId
      : weldIndex === 0
        ? weld1FitmentId
        : weldIndex === 1
          ? weld2FitmentId
          : undefined;
    if (fitmentIdToUse != null && fitmentIdToUse !== '') {
      fitmentData.details.fitmentDocId = Number(fitmentIdToUse);
    }
    console.log('fitmentData', fitmentData);
    setFitmentWeldData(fitmentData);

    return fitmentData;

  }
  const UpdateVisualInspectionData = (productionWelds: any) => {

    const visualInspectionData = productionWelds?.weldVisualInspection ? { ...productionWelds.weldVisualInspection } : {};
    visualInspectionData.productionId = productionSheetData?.productionId;
    visualInspectionData.productionWeldId = productionWelds?.productionWeldId;
    visualInspectionData.projectId = productionWelds?.projectId;
    visualInspectionData.projectTypeId = productionWelds?.projectTypeId;
    visualInspectionData.pipeId = productionWelds?.pipeId;
    if (!visualInspectionData.details) {
      visualInspectionData.details = {};
    }


    setVisualInspectionWeldData(visualInspectionData);

    return visualInspectionData;

  }
  const UpdateAlignmentData = (productionSheetData: any) => {

    if (productionSheetData?.productionRepairAlignments?.length > 1) {
      UpdateRepairAlignmentData(0, 0);
      return;
    }
    const alignmentData = productionSheetData?.productionAlignment ? { ...productionSheetData?.productionAlignment } : {};

    alignmentData.productionId = productionSheetData?.productionId;
    alignmentData.projectId = selectedPipe?.projectId;
    alignmentData.projectTypeId = selectedPipe?.projectTypeId;
    alignmentData.pipeId = selectedPipe?.pipeId;
    alignmentData.repairindex = 0;
    if (!alignmentData.details) {
      alignmentData.details = {};
    }
    setAlignmentWeldData(alignmentData);

    return alignmentData;

  }

  const UpdateRepairAlignmentData = (weldindex: number, repairindex: number) => {

    // Find existing repair alignment from productionSheetData or repairAlignments state
    let alignmentData = productionSheetData?.productionRepairAlignments?.find((ra: any) => ra.repairindex === repairindex)

    //console.log(' existing alignmentData', alignmentData);

    if (!alignmentData) {
      alignmentData = {}
      // Set required properties
      alignmentData.productionId = productionSheetData?.productionId;
      alignmentData.projectId = selectedPipe?.projectId;
      alignmentData.projectTypeId = selectedPipe?.projectTypeId;
      alignmentData.pipeId = selectedPipe?.pipeId;
      alignmentData.repairindex = repairindex;

      if (!alignmentData.details) {
        alignmentData.details = {};
      }
      // productionSheetData?.productionRepairAlignments?.push(alignmentData);
      // Update repairAlignments state if it doesn't exist
      // setRepairAlignments([...productionSheetData?.productionRepairAlignments]);
    }
    // console.log('alignmentData', alignmentData);

    // Set alignmentWeldData so Alignment component can use it
    setAlignmentWeldData(alignmentData);

    return alignmentData;
  }





  const handleSave = async () => {
    try {

      if (currentView === 'main') {
        // Date validation
        if (formData.startDate && formData.endDate) {
          const startDate = new Date(formData.startDate);
          const endDate = new Date(formData.endDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Reset time to start of day

          // // Check if start date is in the past
          // if (startDate < today) {
          //   customNotification.warning('Start date cannot be in the past');
          //   return;
          // }

          // // Check if end date is before or equal to start date
          // if (endDate <= startDate) {
          //   customNotification.warning('End date must be after start date');
          //   return;
          // }

          // Check if end date is at least the next day after start date
          const nextDay = new Date(startDate);
          if (endDate < startDate) {
            Notification.warning('End date cannot be before start date');
            return;
          }
        }


        if (weld1Name == '') {
          Notification.warning('Enter the Weld 1 name');
          return;
        }
        if (weld2Name == '') {
          Notification.warning('Enter the Weld 2 name');
          return;
        }


        const data: any = {
          "productionId": selectedItem?.productionId, // Required
          "projectId": selectedPipe?.projectId, // Required
          "projectTypeId": selectedPipe?.projectTypeId, // Required
          "pipeId": selectedPipe?.pipeId, // Required


        };
        if (productionSheetData?.productionSheetId) {
          data.productionSheetId = productionSheetData?.productionSheetId;
        }
        // Add productionSheetId if it exists (for updates)
        if (productionSheetData?.productionWelds && productionSheetData?.productionWelds.length > 0) {
          data.productionWelds = productionSheetData?.productionWelds;

          // Update first weld if it exists
          if (data.productionWelds[0]) {
            data.productionWelds[0].weldPasses = passConfigurationWeld_1;

            // Aggregate all unique welder IDs from all passes for backward compatibility
            const allWelderIdsWeld1 = new Set<string>();
            if (Array.isArray(passConfigurationWeld_1)) {
              passConfigurationWeld_1.forEach((pass: any) => {
                if (Array.isArray(pass.passWelder)) {
                  pass.passWelder.forEach((id: string) => allWelderIdsWeld1.add(String(id)));
                }
              });
            }
            // // Combine pass welders with dropdown selection - prioritize passes, but include dropdown selection
            // if (Array.isArray(selectedWelder1) && selectedWelder1.length > 0) {
            //   selectedWelder1.forEach((id: string) => allWelderIdsWeld1.add(String(id)));
            // }
            //  const aggregatedWelderIds1 = Array.from(allWelderIdsWeld1);


            data.productionWelds[0].weldindex = 1;
            data.productionWelds[0].repairindex = 0;
            // Always save weldId if we have any welder IDs (from passes or dropdown)
            // if (aggregatedWelderIds1.length > 0) {
            //   data.productionWelds[0].weldId = JSON.stringify(aggregatedWelderIds1.map(id => String(id)));
            // } else {
            //   // Clear weldId if no welders selected
            //   data.productionWelds[0].weldId = null;
            // }
            data.productionWelds[0].weldName = weld1Name || 'weld-001';
            if (weld1PipeId != null) data.productionWelds[0].materialPipeId = Number(weld1PipeId);
            if (weld1PassConfigId != null) data.productionWelds[0].passConfigDocId = Number(weld1PassConfigId);
            data.productionWelds[0].productionWeldExtraDetails = {
              materialPipeId: weld1PipeId != null ? Number(weld1PipeId) : undefined,
              passConfigDocId: weld1PassConfigId != null ? Number(weld1PassConfigId) : undefined,
              fitmentSpecDocId: weld1FitmentId != null ? Number(weld1FitmentId) : undefined,
            };
            // For now, save only first welder ID as number (not array)
            // data.productionWelds[0].welderId = aggregatedWelderIds1.length > 0
            //   ? Number(aggregatedWelderIds1[0])
            //   : null;



            data.productionWelds[0].weldId = selectedWelder1.length > 0 ? JSON.stringify(selectedWelder1.map(id => String(id))) : data.productionWelds[0].weldId;
            //data.productionWelds[0].weldName = weld1Name || 'weld-001';
            // For now, save only first welder ID as number (not array)
            data.productionWelds[0].welderId = selectedWelder1.length > 0 ? Number(selectedWelder1[0]) : data.productionWelds[0].welderI
            // Update selectedWelder1 with aggregated IDs for backward compatibility
            // setSelectedWelder1(aggregatedWelderIds1);
            // // Use current state instead of old data structure
            // data.productionWelds[0].weldFitment = fitmentState;
            // data.productionWelds[0].weldVisualInspection = visualInspec
            // tionState;
            // data.productionWelds[0].weldAlignment = alignmentState;
          }

          // Update second weld if it exists
          if (data.productionWelds[1]) {
            data.productionWelds[1].weldPasses = passConfigurationWeld_2;

            // Aggregate all unique welder IDs from all passes for backward compatibility
            const allWelderIdsWeld2 = new Set<string>();
            if (Array.isArray(passConfigurationWeld_2)) {
              passConfigurationWeld_2.forEach((pass: any) => {
                if (Array.isArray(pass.passWelder)) {
                  pass.passWelder.forEach((id: string) => allWelderIdsWeld2.add(String(id)));
                }
              });
            }
            // Combine pass welders with dropdown selection - prioritize passes, but include dropdown selection
            // if (Array.isArray(selectedWelder2) && selectedWelder2.length > 0) {
            //   selectedWelder2.forEach((id: string) => allWelderIdsWeld2.add(String(id)));
            // }
            //const aggregatedWelderIds2 = Array.from(allWelderIdsWeld2);

            data.productionWelds[1].weldindex = 2;
            data.productionWelds[1].repairindex = 0;

            //   // Always save weldId if we have any welder IDs (from passes or dropdown)
            //   if (aggregatedWelderIds2.length > 0) {
            //     data.productionWelds[1].weldId = JSON.stringify(aggregatedWelderIds2.map(id => String(id)));
            //   } else {
            //     // Clear weldId if no welders selected
            //     data.productionWelds[1].weldId = null;
            //   }
            // //  data.productionWelds[1].weldName = weld2Name || 'weld-002';
            //   // For now, save only first welder ID as number (not array)
            //   data.productionWelds[1].welderId = aggregatedWelderIds2.length > 0
            //     ? Number(aggregatedWelderIds2[0])
            //     : null;


            data.productionWelds[1].weldId = selectedWelder2.length > 0 ? JSON.stringify(selectedWelder2.map(id => String(id))) : data.productionWelds[1].weldId;
            data.productionWelds[1].weldName = weld2Name || 'weld-002';
            if (weld2PipeId != null) data.productionWelds[1].materialPipeId = Number(weld2PipeId);
            if (weld2PassConfigId != null) data.productionWelds[1].passConfigDocId = Number(weld2PassConfigId);
            data.productionWelds[1].productionWeldExtraDetails = {
              materialPipeId: weld2PipeId != null ? Number(weld2PipeId) : undefined,
              passConfigDocId: weld2PassConfigId != null ? Number(weld2PassConfigId) : undefined,
              fitmentSpecDocId: weld2FitmentId != null ? Number(weld2FitmentId) : undefined,
            };
            // For now, save only first welder ID as number (not array)
            data.productionWelds[1].welderId = selectedWelder2.length > 0 ? Number(selectedWelder2[0]) : data.productionWelds[1].welderId;
            // Update selectedWelder2 with aggregated IDs for backward compatibility
            // setSelectedWelder2(aggregatedWelderIds2);
            // // Use current state for second weld as well
            // data.productionWelds[1].weldFitment = fitmentState;
            // data.productionWelds[1].weldVisualInspection = visualInspectionState;
            // data.productionWelds[1].weldAlignment = alignmentState;
          }

          // Add repair welds without modifying existing welds
          // Remove existing repair welds from the array first (keep only default welds 1 and 2)
                  // Add repair welds without modifying existing welds
          // Capture existing repair welds before truncating, to preserve productionWeldId and other data
          const existingRepairWeldsSnapshot = data.productionWelds.slice(2);
          // Remove existing repair welds from the array first (keep only default welds 1 and 2)
          const defaultWelds = data.productionWelds.slice(0, 2);
          data.productionWelds = [...defaultWelds];

          // Add repair welds for weld 1
          for (let repairindex = 1; repairindex <= numberOfRepairsWeld1; repairindex++) {
            const key = getRepairWeldKey(1, repairindex);
            const repairData = repairWelds.get(key);
            if (repairData) {
              // Find existing repair weld from snapshot to preserve productionWeldId and inspection data
              const existingRepairWeld = existingRepairWeldsSnapshot.find(
                (w: any) => w.weldindex === 1 && w.repairindex === repairindex
              );

              const repairWeldData: any = {
                weldindex: 1,
                repairindex: repairindex,
                weldName: repairData.weldName || `Repair-${repairindex}`,
                weldPasses: repairData.passConfiguration || [],
                weldFitment: existingRepairWeld?.weldFitment || {},
                weldVisualInspection: existingRepairWeld?.weldVisualInspection || {},
                weldAlignment: existingRepairWeld?.weldAlignment || {}
              };
              repairWeldData.productionWeldExtraDetails = {
                materialPipeId: repairData.materialPipeId != null ? Number(repairData.materialPipeId) : undefined,
                passConfigDocId: repairData.passConfigDocId != null ? Number(repairData.passConfigDocId) : undefined,
                fitmentSpecDocId: repairData.fitmentSpecDocId != null ? Number(repairData.fitmentSpecDocId) : undefined,
              };

              // Preserve productionWeldId from the map (populated during load) or from the snapshot
              const savedId = repairData.productionWeldId ?? existingRepairWeld?.productionWeldId ?? null;
              if (savedId) {
                repairWeldData.productionWeldId = savedId;
              }

              data.productionWelds.push(repairWeldData);
            }
          }

          // Add repair welds for weld 2
          for (let repairindex = 1; repairindex <= numberOfRepairsWeld2; repairindex++) {
            const key = getRepairWeldKey(2, repairindex);
            const repairData = repairWelds.get(key);
            if (repairData) {
              // Find existing repair weld from snapshot to preserve productionWeldId and inspection data
              const existingRepairWeld = existingRepairWeldsSnapshot.find(
                (w: any) => w.weldindex === 2 && w.repairindex === repairindex
              );

              const repairWeldData: any = {
                weldindex: 2,
                repairindex: repairindex,
                weldName: repairData.weldName || `Repair-${repairindex}`,
                weldPasses: repairData.passConfiguration || [],
                weldFitment: existingRepairWeld?.weldFitment || {},
                weldVisualInspection: existingRepairWeld?.weldVisualInspection || {},
                weldAlignment: existingRepairWeld?.weldAlignment || {}
              };
              repairWeldData.productionWeldExtraDetails = {
                materialPipeId: repairData.materialPipeId != null ? Number(repairData.materialPipeId) : undefined,
                passConfigDocId: repairData.passConfigDocId != null ? Number(repairData.passConfigDocId) : undefined,
                fitmentSpecDocId: repairData.fitmentSpecDocId != null ? Number(repairData.fitmentSpecDocId) : undefined,
              };

              // Preserve productionWeldId from the map (populated during load) or from the snapshot
              const savedId = repairData.productionWeldId ?? existingRepairWeld?.productionWeldId ?? null;
              if (savedId) {
                repairWeldData.productionWeldId = savedId;
              }

              data.productionWelds.push(repairWeldData);
            }
          }
        }
        else {
          // Aggregate all unique welder IDs from all passes for weld 1
          const allWelderIdsWeld1 = new Set<string>();
          if (Array.isArray(passConfigurationWeld_1)) {
            passConfigurationWeld_1.forEach((pass: any) => {
              if (Array.isArray(pass.passWelder)) {
                pass.passWelder.forEach((id: string) => allWelderIdsWeld1.add(String(id)));
              }
            });
          }
          // // Combine pass welders with dropdown selection
          // if (Array.isArray(selectedWelder1) && selectedWelder1.length > 0) {
          //   selectedWelder1.forEach((id: string) => allWelderIdsWeld1.add(String(id)));
          // }
          //  const aggregatedWelderIds1 = Array.from(allWelderIdsWeld1);

          // Aggregate all unique welder IDs from all passes for weld 2
          const allWelderIdsWeld2 = new Set<string>();
          if (Array.isArray(passConfigurationWeld_2)) {
            passConfigurationWeld_2.forEach((pass: any) => {
              if (Array.isArray(pass.passWelder)) {
                pass.passWelder.forEach((id: string) => allWelderIdsWeld2.add(String(id)));
              }
            });
          }
          // // Combine pass welders with dropdown selection
          // if (Array.isArray(selectedWelder2) && selectedWelder2.length > 0) {
          //   selectedWelder2.forEach((id: string) => allWelderIdsWeld2.add(String(id)));
          // }
          //  const aggregatedWelderIds2 = Array.from(allWelderIdsWeld2);

          data.productionWelds = [       // Optional - Array of weld objects
            {
              "weldName": weld1Name || 'weld-001', // Required for new welds
              "weldindex": 1,
              "repairindex": 0,
              //"weldId": aggregatedWelderIds1.length > 0 ? JSON.stringify(aggregatedWelderIds1.map(id => String(id))) : null, // Save full array as JSON string
              // "welderId": aggregatedWelderIds1.length > 0 ? Number(aggregatedWelderIds1[0]) : null, // Optional - first welder ID as number
              "weldId": selectedWelder1.length > 0 ? JSON.stringify(selectedWelder1.map(id => String(id))) : null, // Array as string
              "welderId": selectedWelder1.length > 0 ? Number(selectedWelder1[0]) : null, // Optional - first welder ID as number
              "weldPasses": passConfigurationWeld_1 || [],
              "weldFitment": {},
              "weldVisualInspection": {},
              "weldAlignment": {},
              "productionWeldExtraDetails": {
                materialPipeId: weld1PipeId != null ? Number(weld1PipeId) : undefined,
                passConfigDocId: weld1PassConfigId != null ? Number(weld1PassConfigId) : undefined,
                fitmentSpecDocId: weld1FitmentId != null ? Number(weld1FitmentId) : undefined,
              },
            },
            {
              "weldName": weld2Name || 'weld-002', // Required for new welds
              "weldindex": 2,
              "repairindex": 0,
              //"weldId": aggregatedWelderIds2.length > 0 ? JSON.stringify(aggregatedWelderIds2.map(id => String(id))) : null, // Save full array as JSON string
              //"welderId": aggregatedWelderIds2.length > 0 ? Number(aggregatedWelderIds2[0]) : null, // Optional - first welder ID as number
              "weldId": selectedWelder2.length > 0 ? JSON.stringify(selectedWelder2.map(id => String(id))) : null, // Array as string
              "welderId": selectedWelder2.length > 0 ? Number(selectedWelder2[0]) : null, // Optional - first welder ID as number
              "weldPasses": passConfigurationWeld_2 || [],
              "weldFitment": {},
              "weldVisualInspection": {},
              "weldAlignment": {},
              "productionWeldExtraDetails": {
                materialPipeId: weld2PipeId != null ? Number(weld2PipeId) : undefined,
                passConfigDocId: weld2PassConfigId != null ? Number(weld2PassConfigId) : undefined,
                fitmentSpecDocId: weld2FitmentId != null ? Number(weld2FitmentId) : undefined,
              },
            }
          ];

          // Add repair welds for weld 1
          for (let repairindex = 1; repairindex <= numberOfRepairsWeld1; repairindex++) {
            const key = getRepairWeldKey(1, repairindex);
            const repairData = repairWelds.get(key);
            if (repairData) {
              const repairWeldEntry: any = {
                weldindex: 1,
                repairindex: repairindex,
                weldName: repairData.weldName || `Repair-${repairindex}`,
                weldPasses: repairData.passConfiguration || [],
                weldFitment: {},
                weldVisualInspection: {},
                weldAlignment: {}
              };
              if (repairData.productionWeldId) {
                repairWeldEntry.productionWeldId = repairData.productionWeldId;
              }
              data.productionWelds.push(repairWeldEntry);
            }
          }

          // Add repair welds for weld 2
          for (let repairindex = 1; repairindex <= numberOfRepairsWeld2; repairindex++) {
            const key = getRepairWeldKey(2, repairindex);
            const repairData = repairWelds.get(key);
            if (repairData) {
              const repairWeldEntry: any = {
                weldindex: 2,
                repairindex: repairindex,
                weldName: repairData.weldName || `Repair-${repairindex}`,
                weldPasses: repairData.passConfiguration || [],
                weldFitment: {},
                weldVisualInspection: {},
                weldAlignment: {}
              };
              if (repairData.productionWeldId) {
                repairWeldEntry.productionWeldId = repairData.productionWeldId;
              }
              data.productionWelds.push(repairWeldEntry);
            }
          }

          // Update selectedWelder1 and selectedWelder2 with aggregated IDs for backward compatibility
          //  setSelectedWelder1(aggregatedWelderIds1);
          //  setSelectedWelder2(aggregatedWelderIds2);
        }

        // Add all fields with proper validation
        // Always include wpsDocId, even if null (cleared)
        data.wpsDocId = selectedWpsId ? Number(selectedWpsId) : null;

        // Always include drawingDocId, even if null (cleared)
        data.drawingDocId = selectedDrawingId ? Number(selectedDrawingId) : null;

        if (formData.startDate) {
          data.startDate = formData.startDate;
        }

        if (formData.endDate) {
          data.endDate = formData.endDate;
        }

        // Always include materialGasIds, even if empty array
        data.materialGasIds = selectedGasIds ? selectedGasIds.map(id => Number(id)) : [];

        // Always include materialForgeId, even if null (cleared)
        if (selectedForgeId) {
          const isValidForgeId = forgeOptions.find(option => option.value === Number(selectedForgeId));
          if (isValidForgeId?.status === 'Consumed') {
            Notification.warning('selected Forge is already consumed', 'please select available Forge');
            return;
          }
          data.materialForgeId = Number(selectedForgeId);
        } else {
          data.materialForgeId = null;
        }

        // Always include materialWireLotIds, even if empty array
        data.materialWireLotIds = selectedWireIds ? selectedWireIds.map(id => Number(id)) : [];

        // Always include materialPipeId, even if null (cleared)
        if (selectedPipeId) {
          const isValidPipeId = pipeOptions.find(option => option.value === Number(selectedPipeId));
          if (isValidPipeId?.status === 'Consumed') {
            Notification.warning('selected Pipe is already consumed', 'please select available Pipe');
            return;
          }
          data.materialPipeId = Number(selectedPipeId);
        } else {
          data.materialPipeId = null;
        }

        if (distanceFromPipeEnd && Number(distanceFromPipeEnd) > 0) {
          data.distanceFromPipeEndToForge = convertSetupValueToInches(Number(distanceFromPipeEnd));
        }

        // Add production status if it exists
        if (productionStatus) {
          data.productionStatus = productionStatus;
        }

        // Add pass configuration ID if selected
        if (selectedPassConfigurationId) {
          data.passConfigDocId = Number(selectedPassConfigurationId);
        }
        if (selectedCustomerRepresentativeId) {
          data.customerRepId = Number(selectedCustomerRepresentativeId);
        }
        if (selectedQcInspectorId) {
          data.qcInspectorId = Number(selectedQcInspectorId);
        }

        //if (productionSheetData?.signature) 
        {
          data.signature = productionSheetData?.signature;
        }

        // // Create productionRepairAlignments array for repair welds if productionAlignment exists
        // // Each repair index has one common alignment for both weld 1 and weld 2
        // if (productionSheetData?.productionAlignment && data.productionWelds) {
        //   const repairWelds = data.productionWelds.filter((w: any) => w.repairindex > 0);
        //   if (repairWelds.length > 0) {
        //     // Get unique repair indices (each repair index has one common alignment)
        //     const uniqueRepairIndices = Array.from(new Set(repairWelds.map((w: any) => w.repairindex))) as number[];

        //     data.productionRepairAlignments = uniqueRepairIndices.map((repairindex: number) => {
        //       // Find existing repair alignment by repairindex only (common for both welds)
        //       const existingRepairAlignment = productionSheetData?.productionRepairAlignments?.find(
        //         (ra: any) => ra.repairindex === repairindex
        //       );

        //       // Create alignment object based on productionAlignment structure
        //       // No weldindex - common alignment for both weld 1 and weld 2
        //       const repairAlignment = existingRepairAlignment || {
        //         productionId: productionSheetData?.productionId,
        //         projectId: selectedPipe?.projectId,
        //         projectTypeId: selectedPipe?.projectTypeId,
        //         pipeId: selectedPipe?.pipeId,
        //         repairindex: repairindex,
        //         details: productionSheetData.productionAlignment.details ? { ...productionSheetData.productionAlignment.details } : {}
        //       };

        //       // Preserve existing alignment data if it exists
        //       if (existingRepairAlignment) {
        //         return {
        //           ...existingRepairAlignment,
        //           repairindex: repairindex
        //         };
        //       }

        //       return repairAlignment;
        //     });
        //   }
        // }

        setIsSaveLoading(true)
        // Dispatch the action to save data
        await dispatch(updateAssemblyStatus(data));

        setHasUnsavedChanges(false);
        setIsProductionSheetEditMode(false);
      }
      else if (currentView === 'fitment') {
        const fitmentData = fitmentWeldData ? fitmentWeldData : (productionSheetData?.productionWelds && productionSheetData.productionWelds[selectedWeld] ? UpdateFitmentData(productionSheetData.productionWelds[selectedWeld]) : null);

        if (fitmentData) {
          setIsSaveLoading(true)
          // Dispatch the action to save fitment data
          await dispatch(updateAssemblyFitment(fitmentData));
          setHasUnsavedChanges(false);
        }
      }
      else if (currentView === 'visual') {
        const visualInspectionData = visualInspectionWeldData ? visualInspectionWeldData : (productionSheetData?.productionWelds && productionSheetData.productionWelds[selectedWeld] ? UpdateVisualInspectionData(productionSheetData.productionWelds[selectedWeld]) : null);

        if (visualInspectionData) {
          setIsSaveLoading(true)
          //  visualInspectionData.weldPasses.splice(0, 1);
          await dispatch(updateAssemblyVisualInspection(visualInspectionData));

          setHasUnsavedChanges(false);
        }
      }
      else if (currentView === 'alignment') {

        if (isRepairAlignmentView) {
          const alignmentData = alignmentWeldData; //repairAlignments.find((ra: any) => ra.repairindex === selectedRepairIndex);
          // console.log('alignmentData', alignmentData, alignmentWeldData);
          if (alignmentData) {
            setIsSaveLoading(true)
            await dispatch(updateAssemblyAlignment(alignmentData));
            setHasUnsavedChanges(false);
          }
        } else {
          const alignmentData = alignmentWeldData ? alignmentWeldData : (productionSheetData && productionSheetData?.productionAlignment ? UpdateAlignmentData(productionSheetData) : null);
          if (alignmentData) {
            setIsSaveLoading(true)
            await dispatch(updateAssemblyAlignment(alignmentData));
            setHasUnsavedChanges(false);
          }
        }
      }


    } catch (error) {
    }
  };


  const handleStatusChange = (value: string) => {
    setProductionStatus(value);
    // Update the selectedItem directly since it's passed as a prop
    if (selectedItem) {
      selectedItem.productionStatus = value;
    }
    markAsUnsaved();
  };

  // Callback function to update fitment state from child component
  const handleFitmentStateChange = (newFitmentState: any) => {
    //setFitmentState(newFitmentState);
    markAsUnsaved();
  };

  // Callback function to update visual inspection state from child component
  const handleVisualInspectionStateChange = (newVisualInspectionState: any) => {
    //  setVisualInspectionState(newVisualInspectionState);
    markAsUnsaved();
  };

  // Callback function to update alignment state from child component
  const handleAlignmentStateChange = (newAlignmentState: any) => {
    // setAlignmentState(newAlignmentState);
    markAsUnsaved();
  };

  const handleQcSignatureChange = (value: string | null) => {
    setSelectedQcInspectorId(value);
    markAsUnsaved();
  };

  const handleCustomerSignatureChange = (value: string | null) => {
    setSelectedCustomerRepresentativeId(value);
    markAsUnsaved();
  };

  const OnHandleSIngaure = (comment: { signature: string; name: string; comment?: string; stampImage?: string; type: 'customer' | 'qc' }) => {
    if (!productionSheetData) {
      console.error('productionSheetData is null or undefined');
      return;
    }

    // Initialize signature object if it doesn't exist
    if (!productionSheetData.signature) {
      productionSheetData.signature = {};
    }

    productionSheetData.signature = {
      ...productionSheetData.signature,
      [comment.type]: {
        signature: comment.signature,
        name: comment.name,
        comment: comment.comment,
        stampImage: comment.stampImage,
        timestamp: new Date().toISOString()
      }
    };
    markAsUnsaved();
  };


  const handleConfirmDeleteRepairWeld = async () => {
    try {
      if (!itemToDeleteRepairWeld) {
        Notification.warning('Invalid repair weld data');
        return;
      }

      setDeleteRepairWeldLoading(true);

      // Get the productionWeldId from the repair weld data (if it exists - might be unsaved)
      const productionWeldId = itemToDeleteRepairWeld.productionWeldId;

      // If the repair weld has been saved (has productionWeldId), delete it from the API
      if (productionWeldId) {
        const productionWeldIds = [productionWeldId];

        // Dispatch the delete action
        dispatch(deleteRepairWeld({ productionWeldIds }));

        // Wait a bit for the saga to complete
        await new Promise(resolve => setTimeout(resolve, 500));

        // Reload production data to refresh the UI
        await loadProductionData();
      }

      // Remove from local state (whether saved or not)
      const weldindex = itemToDeleteRepairWeld.weldindex;
      const repairindex = itemToDeleteRepairWeld.repairindex;
      const key = getRepairWeldKey(weldindex, repairindex);
      const newRepairWelds = new Map(repairWelds);
      newRepairWelds.delete(key);
      setRepairWelds(newRepairWelds);

      // Update repair counts
      if (weldindex === 1) {
        setNumberOfRepairsWeld1(Math.max(0, numberOfRepairsWeld1 - 1));
      } else if (weldindex === 2) {
        setNumberOfRepairsWeld2(Math.max(0, numberOfRepairsWeld2 - 1));
      }

      setIsDeleteRepairWeldModalVisible(false);
      setItemToDeleteRepairWeld(null);
      setDeleteRepairWeldLoading(false);

      //Notification.success('Repair weld deleted successfully');
    } catch (error: any) {
      setDeleteRepairWeldLoading(false);
      Notification.error('Failed to delete repair weld', error?.message || 'An error occurred');
    }
  };

  const handleSaveAndClose = async () => {
    await handleSave();
    await handleBackNavigation();
    setIsConfirmDialogOpen(false);
  };

  // File type detection utilities
  const getFileExtension = (fileName?: string, fileUrl?: string) => {
    const name = fileName || fileUrl?.split('?')[0].split('/').pop() || '';
    return name.split('.').pop()?.toLowerCase() || '';
  };

  const getViewerType = (file: any): 'pdf' | 'media' | 'document' => {
    const ext = getFileExtension(file?.fileName, file?.fileUrl);
    if (ext === 'pdf') return 'pdf';
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'tiff', 'tif'];
    const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'webm'];
    if (imageExts.includes(ext) || videoExts.includes(ext)) return 'media';
    return 'document'; // Default for office docs and fallback
  };

  // Unified viewer handler
  const openViewer = (fileOrFiles: any | any[]) => {
    if (!fileOrFiles) return;

    // Handle array of files
    if (Array.isArray(fileOrFiles)) {
      const validFiles = fileOrFiles.filter((f: any) => f?.fileUrl || f?.file_url);
      if (validFiles.length === 0) {
        Notification.error('No valid files to view');
        return;
      }

      // Check if all files are media (images/videos)
      const allMedia = validFiles.every((file: any) => getViewerType(file) === 'media');

      if (allMedia && validFiles.length > 1) {
        // Multiple media files - use MediaViewer
        setViewerFiles(validFiles);
        setViewerType('media');
        setViewerFile(null);
      } else {
        // Single file or mixed types - open first file
        openViewer(validFiles[0]);
      }
      return;
    }

    // Handle single file
    const file = fileOrFiles;
    if (!file?.fileUrl && !file?.file_url) {
      Notification.error('File URL not available');
      return;
    }

    // Normalize file object to ensure fileUrl exists
    const normalizedFile = {
      ...file,
      fileUrl: file.fileUrl || file.file_url || file.url,
      fileName: file.fileName || file.file_name || file.name
    };

    const type = getViewerType(normalizedFile);
    setViewerType(type);

    if (type === 'media') {
      // For media, use array format for MediaViewer
      setViewerFiles([normalizedFile]);
      setViewerFile(null);
    } else {
      // For PDF and documents, use single file
      setViewerFile(normalizedFile);
      setViewerFiles([]);
    }
  };

  const closeViewer = () => {
    setViewerType(null);
    setViewerFile(null);
    setViewerFiles([]);
  };

  // Handle download fitment report
  // const handleDownloadFitmentReport = () => {
  //   // console.log('===== DOWNLOAD REPORT CLICKED =====');
  //   // console.log('project:', project);
  //   // console.log('selectedItem:', selectedItem);
  //   // console.log('selectedPipe:', selectedPipe);
  //   // console.log('fitmentWeldData:', fitmentWeldData);
  //   // console.log('currentView:', currentView);

  //   // Use actual project ID from props instead of DATA360 API
  //   const projectId = project?.id || project?.projectId || project?.project?.projectId;
  //   const pipeId = selectedPipe?.id || selectedPipe?.pipeId;

  //   // Validate required data
  //   if (!projectId) {
  //     console.error('Project ID not found');
  //     Notification.error('Project ID not found');
  //     return;
  //   }
  //   if (!pipeId) {
  //     console.error('Pipe ID not found');
  //     Notification.error('Pipe ID not found');
  //     return;
  //   }
  //   if (!fitmentWeldData?.productionId) {
  //     console.error('Production ID not found');
  //     Notification.error('Production ID not found');
  //     return;
  //   }
  //   if (!fitmentWeldData?.productionWeldFitmentId) {
  //     console.error('Fitment ID not found');
  //     Notification.error('Fitment ID not found');
  //     return;
  //   }
  //   if (!fitmentWeldData?.productionWeldId) {
  //     console.error('Weld ID not found');
  //     Notification.error('Weld ID not found');
  //     return;
  //   }

  //   const payload = {
  //     projectId: projectId,
  //     pipeId: pipeId,
  //     productionId: fitmentWeldData.productionId,
  //     productionWeldFitmentId: fitmentWeldData.productionWeldFitmentId,
  //     productionWeldId: fitmentWeldData.productionWeldId
  //   };

  // //  console.log('Dispatching downloadFitmentReport with payload:', payload);

  //   // Dispatch Redux action
  //   dispatch(downloadFitmentReport(payload));

  //  // console.log('Download action dispatched');
  // };

  // // Listen for fitment report download success
  // useEffect(() => {

  //   if (fitmentReportDownloadData && fitmentReportDownloadData.downloadUrl) {
  //   //  console.log('Download URL received:', fitmentReportDownloadData.downloadUrl);

  //     // Download the PDF by opening in new tab
  //     const link = document.createElement('a');
  //     link.href = fitmentReportDownloadData.downloadUrl;
  //     link.target = '_blank';
  //     link.rel = 'noopener noreferrer';
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);

  //     console.log('PDF link clicked, clearing download data...');

  //     // Clear the download data after a short delay to allow the link to open
  //     setTimeout(() => {
  //       dispatch(clearFitmentReportDownload());
  //     }, 500);
  //   }
  // }, [fitmentReportDownloadData, fitmentReportDownloadLoading, fitmentReportDownloadError, dispatch]);

  // Listen for production sheet main report download success
  // useEffect(() => {
  //   if (productionSheetMainReportDownloadData && productionSheetMainReportDownloadData.downloadUrl) {
  //     // // Download the PDF by opening in new tab
  //     // const link = document.createElement('a');
  //     // link.href = productionSheetMainReportDownloadData.downloadUrl;
  //     // link.target = '_blank';
  //     // link.rel = 'noopener noreferrer';
  //     // document.body.appendChild(link);
  //     // link.click();
  //     // document.body.removeChild(link);

  //     // Clear the download data after a short delay to allow the link to open
  //     setTimeout(() => {
  //       dispatch(clearProductionSheetMainReportDownload());
  //     }, 500);
  //   }
  // }, [productionSheetMainReportDownloadData, productionSheetMainReportDownloadLoading, productionSheetMainReportDownloadError, dispatch]);

  // Check if signatures exist in fitment data
  const hasFitmentSignatures = () => {
    // Check if both QC and customer signatures exist
    return fitmentWeldData?.signature?.qc?.signature && fitmentWeldData?.signature?.customer?.signature;
  };

  const hasVisualInspectionSignatures = () => {
    // Check if both QC and customer signatures exist
    return visualInspectionWeldData?.signature?.qc?.signature && visualInspectionWeldData?.signature?.customer?.signature;
  };

  const hasAlignmentSignatures = () => {
    // Check if both QC and customer signatures exist
    return alignmentWeldData?.signature?.qc?.signature && alignmentWeldData?.signature?.customer?.signature;
  };

  // Check if signatures exist in production sheet data
  const hasProductionSheetSignatures = () => {
    return productionSheetData?.signature?.qc?.signature && productionSheetData?.signature?.customer?.signature;
  };

  // Handle production sheet main report download
  const handleDownloadProductionSheetMainReport = async () => {
    setIsReportLoading(true);
    try {
      const projectId = project?.id || project?.projectId || project?.project?.projectId;
      const pipeId = selectedPipe?.id || selectedPipe?.pipeId;
      const productionId = selectedItem?.productionId;

      if (projectId && pipeId && productionId) {

        // Build the API URL with query parameters
        const params = new URLSearchParams({
          projectId: projectId.toString(),
          pipeId: pipeId.toString(),
          productionId: productionId.toString()
        });

       const url = `/download-proxy/reports/production-sheet-main-report?${params.toString()}`;
       //const url = `/reports/production-sheet-main-report?${params.toString()}`;

        const res = await axiosConfig.get(url);

        if (res.status === 200 && res.data.success === 1 && res.data.data?.downloadUrl) {
          loadProductionData();
        } else {
          Notification.error('Failed to generate report');
        }
      }
    } catch (error) {
      Notification.error('Error downloading report');
    } finally {
      // Clear download state
      setIsReportLoading(false);
    }
  };

  // Check if signatures exist for current view
  const hasSignatures = () => {

    // if(isEditMode) {
    //   return false;
    // }

    if (currentView === 'fitment') {
      return fitmentWeldData?.signature?.qc?.signature && fitmentWeldData?.signature?.customer?.signature || isEditMode;
    } else if (currentView === 'visual') {
      return visualInspectionWeldData?.signature?.qc?.signature && visualInspectionWeldData?.signature?.customer?.signature || isEditMode;
    } else if (currentView === 'alignment') {
      return alignmentWeldData?.signature?.qc?.signature && alignmentWeldData?.signature?.customer?.signature || isEditMode;
    } else if (currentView === 'main') {
      return hasProductionSheetSignatures() || isProductionSheetEditMode;
    }
    return false;
  };

  // Handle edit button click
  const handleEdit = () => {
    setShowEditConfirmation(true);
  };

  // Handle confirm edit - clear signatures and enable edit mode
  const handleConfirmEdit = () => {
    setShowEditConfirmation(false);
    setIsEditMode(true);

    // Clear signatures when entering edit mode
    if (currentView === 'fitment' && fitmentWeldData) {
      const updatedData = { ...fitmentWeldData };
      updatedData.signature = null;
      setFitmentWeldData(updatedData);
    } else if (currentView === 'visual' && visualInspectionWeldData) {
      const updatedData = { ...visualInspectionWeldData };
      updatedData.signature = null;
      setVisualInspectionWeldData(updatedData);
    } else if (currentView === 'alignment' && alignmentWeldData) {
      const updatedData = { ...alignmentWeldData };
      updatedData.signature = null;
      setAlignmentWeldData(updatedData);
    } else if (currentView === 'main' && productionSheetData) {
      const updatedData = { ...productionSheetData };
      updatedData.signature = null;
      setProductionSheetData(updatedData);
    }

    // Mark as having unsaved changes after clearing signatures
    // setHasUnsavedChanges(true);
  };
  const handleConfirmProductionSheetEdit = () => {
    setShowEditConfirmation(false);
    setIsProductionSheetEditMode(true);

    // if (currentView === 'main' && productionSheetData) {
    //   const updatedData = { ...productionSheetData };
    //   updatedData.signature = null;
    //   productionSheetData.signature = null;
    //   setProductionSheetData(updatedData);
    // }

    // Mark as having unsaved changes after clearing signatures
    // setHasUnsavedChanges(true);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setShowEditConfirmation(false);

    // Stay in view mode (isEditMode remains false)
  };


  const getWeldData = (weldindex: number, repairindex: number) => {
    const key = getRepairWeldKey(weldindex, repairindex);
    let repairData = repairWelds.get(key);

    // Get base pass configuration from the parent weld
    const basePassConfig = weldindex === 1 ? passConfigurationWeld_1 : passConfigurationWeld_2;

    // Initialize repair pass configuration if empty
    if (!repairData || repairData.passConfiguration.length === 0) {
      if (basePassConfig.length > 0) {
        const initializedPasses = basePassConfig.map((pass: any) => ({
          ...pass,
          passStatus: false,
          passTemperature: null,
          passWelder: []
        }));
        repairData = { weldName: '', passConfiguration: initializedPasses };
        setRepairWelds(new Map(repairWelds.set(key, repairData)));
      } else {
        repairData = { weldName: '', passConfiguration: [] };
      }
    }

    const passConfig = repairData.passConfiguration.length > 0 ? repairData.passConfiguration : basePassConfig;

    // Find the repair weld in productionSheetData if it exists
    const repairWeldData = productionSheetData?.productionWelds?.find(
      (w: any) => w.weldindex === weldindex && w.repairindex === repairindex
    );
    //  console.log('repairWeldData', weldindex, repairindex, repairWeldData);
    return (
      <>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Header row with weld name and identifier */}
          <div className="flex flex-col items-center justify-center min-w-[60px]">
            <h4 className="text-sm font-semibold text-blue-800">Repair {repairindex} (Weld {weldindex})</h4>
          </div>
          <div className="flex items-center gap-2 flex-1">
            <Input
              id={`repairWeldName-${weldindex}-${repairindex}`}
              value={repairData.weldName || ''}
              onChange={(e) => {
                handleRepairWeldNameChange(weldindex, repairindex, e.target.value);
              }}
              placeholder="Enter Weld Name"
              className="w-full max-w-[200px]"
              disabled={!canEdit || !isProductionSheetEditMode}
            />
            <Button
              variant="outline"
              size="icon"
              className="shrink-0 h-10 w-10"
              onClick={() => handleQRScan('repair-weld', weldindex, repairindex)}
              disabled={!canEdit || !isProductionSheetEditMode}
            >
              <QrCode className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="shrink-0 h-10 w-10"
              onClick={() => { setWeldSectionDialogWeldIndex(weldindex as 1 | 2); setWeldSectionDialogRepairIndex(repairindex); }}
              title="Repair section: Pipe NRI, Pass config, Fitment"
              disabled={!canEdit || !isProductionSheetEditMode}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
          {/* Pills: Pipe NRI, Pass Configuration, Fitment for this repair */}
          {repairData && (repairData.materialPipeId != null || repairData.passConfigDocId != null || repairData.fitmentSpecDocId != null) && (
            <div className="flex flex-wrap gap-2 mt-2 w-full">
              {(() => {
                const data = repairData!;
                const pipeLabel = data.materialPipeId ? (pipeOptions.find((o: any) => o.value === data.materialPipeId)?.label ?? '-') : 'None';
                const passLabel = data.passConfigDocId ? (passConfigurationOptions.find((o: any) => String(o.value) === String(data.passConfigDocId))?.label ?? 'Pass') : '—';
                const fitLabel = data.fitmentSpecDocId ? (fitmentOptions.find((o: any) => String(o.value) === String(data.fitmentSpecDocId))?.label ?? 'Fitment') : 'None';
                const items = [
                  { label: 'Pipe NRI', value: pipeLabel, bgColor: 'bg-slate-100 text-slate-700' },
                  { label: 'Pass Configuration', value: passLabel, bgColor: 'bg-amber-50 text-amber-800' },
                  { label: 'Fitment', value: fitLabel, bgColor: 'bg-emerald-50 text-emerald-800' },
                ];
                return items.map((item, idx) => (
                  <span key={idx} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                    {item.label}: {item.value}
                  </span>
                ));
              })()}
            </div>
          )}

          {/* Buttons row */}
          {repairWeldData ? (
            <div className="flex items-center gap-2">
              {/* <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  setIsRepairAlignmentView(true);
                  setSelectedRepairIndex(repairindex);
                  await UpdateRepairAlignmentData(weldindex, repairindex);
                  setCurrentView('alignment');
                  setSelectedWeld(0);
                }}
                disabled={!repairWeldData}
                className="flex items-center gap-2 text-xs"
              >
                <Checkbox checked={!!(repairWeldData?.weldAlignment?.qcInspectorId || productionSheetData?.productionRepairAlignments?.find((ra: any) => ra.repairindex === repairindex)?.qcInspectorId)} disabled />
                Alignment
              </Button> */}
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  await UpdateFitmentData(repairWeldData);
                  setCurrentView('fitment');
                  setSelectedWeld(0);
                }}
                disabled={!repairWeldData}
                className="flex items-center gap-2 text-xs"
              >
                <Checkbox checked={!!repairWeldData?.weldFitment?.qcInspectorId} disabled />
                Fitment
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  await UpdateVisualInspectionData(repairWeldData);
                  setCurrentView('visual');
                  setSelectedWeld(0);
                }}
                disabled={!repairWeldData}
                className="flex items-center gap-2 text-xs"
              >
                <Checkbox checked={!!repairWeldData?.weldVisualInspection?.qcInspectorId} disabled />
                Visual Inspection
              </Button>

              <Button
                className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                onClick={() => {
                  setIsDeleteRepairWeldModalVisible(true);
                  setItemToDeleteRepairWeld(repairWeldData)
                }}
                title="Delete Repair Weld"
                variant="outline"
                disabled={!canEdit || !isProductionSheetEditMode}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )
            :
            (
              <div className="flex items-center gap-2">
                <Tooltip content="Save production details to create fitment" placement="top">
                  <Button size="sm" variant="outline" className="flex items-center gap-2 text-xs " disabled>
                    <Checkbox disabled />
                    Fitment
                  </Button>
                </Tooltip>
                <Tooltip content="Save production details to create visual inspection" placement="top">
                  <Button size="sm" variant="outline" className="flex items-center gap-2 text-xs" disabled>
                    <Checkbox disabled />
                    Visual Inspection
                  </Button>
                </Tooltip>
                {/* <Tooltip content="Save production details to create alignment" placement="top">
                  <Button size="sm" variant="outline" className="flex items-center gap-2 text-xs" disabled>
                    <Checkbox disabled />
                    Alignment
                  </Button>
                </Tooltip> */}
              </div>
            )
          }


        </div>
        {/* Common Welder Selection - applies to all passes */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-gray-700">Welder</Label>
          <div className="w-full">
            <div className={!canEdit || !isProductionSheetEditMode ? "pointer-events-none opacity-50" : ""}>
              <MultiSelect
                options={welderList}
                selected={getRepairCommonWelders(weldindex, repairindex)}
                onChange={(selected) => handleRepairWelderChange(weldindex, repairindex, selected)}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-12 items-center border-b border-gray-200 pb-3 gap-2">
            <span className="text-sm font-medium sm:col-span-4">Passes</span>
            <span className="text-sm font-medium sm:col-span-2 text-center">Status</span>
            <span className="text-sm font-medium sm:col-span-6 text-center">Pre heat or interpass temperature (°C)</span>
            {/* <span className="text-sm font-medium sm:col-span-5 text-center">Welder</span> */}
          </div>
          {passConfig.length > 0 ? passConfig.map((pass: any, passIndex: number) => (
            <div key={passIndex} className="grid grid-cols-1 sm:grid-cols-12 items-center border-b border-gray-200 pb-4 gap-2">
              <span className="text-sm font-medium sm:col-span-4">{pass.passName}</span>
              <div className="sm:col-span-2 flex justify-center">
                <Checkbox checked={pass.passStatus || false}
                  onCheckedChange={(checked) => handleRepairPassStatusChange(weldindex, repairindex, checked, passIndex)}
                  disabled={!canEdit || !isProductionSheetEditMode} />
              </div>
              <div className="sm:col-span-6 flex justify-center">
                <Input
                  type="number"
                  placeholder="Temperature"
                  className="w-full max-w-48"
                  value={pass.passTemperature || ''}
                  onChange={(e) => handleRepairPassTemperatureChange(weldindex, repairindex, e, passIndex)}
                  disabled={!canEdit || !isProductionSheetEditMode}
                />
              </div>
              {/* <div className="sm:col-span-5 flex justify-center">
                <div className={!canEdit || !isProductionSheetEditMode ? "pointer-events-none opacity-50 w-full" : "w-full"}>
                  <MultiSelect
                    options={welderList}
                    selected={pass.passWelder || []}
                    onChange={(selected) => handleRepairPassWelderChange(weldindex, repairindex, selected, passIndex)}
                  />
                </div>
              </div> */}
            </div>
          )) : (
            <div className="grid grid-cols-1 sm:grid-cols-12 items-center border-b border-gray-200 pb-4 gap-2">
              <h3 className="text-lg font-medium text-gray-900 mb-2 sm:col-span-12 text-center">No passes created yet</h3>
              <p className="text-gray-500 max-w-sm sm:col-span-12 text-center mx-auto">
                You need to create passes in the specifications. Go to specification → add new pass configuration.
              </p>
            </div>
          )}
        </div>
      </>
    )
  }

  // Render main view with navigation buttons
  if (currentView === 'main') {
    return (
      <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={handleBackNavigation}
                className="hover:bg-gray-100 p-2 rounded-lg transition-colors"
                title="Go back"
                variant="outline"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h2 className="text-xl font-semibold">{selectedItem?.assemblyNumber}</h2>
                <p className="text-sm text-gray-500">
                  {selectedPipe?.size}" × {selectedPipe?.wallThickness}" Buckle Arrestor
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">

              <Button
                onClick={handleBackNavigation}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                variant="outline"
              >
                Cancel
              </Button>
              {!isProductionSheetEditMode && hasSignatures() && canEdit && (
                <Button
                  onClick={handleEdit}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  variant="outline"
                  disabled={productionDataLoading || isReportLoading}
                  loadingText="Generating..."
                  loading={isReportLoading}
                >
                  Edit
                </Button>
              )}

              <Button
                onClick={() => setIsDocumentApprovalModalOpen(true)}
                className="px-4 py-2 text-white bg-[#EB1C37] rounded-lg hover:bg-[#EB1C37]/90"
                variant="default"
                loading={isReportLoading}
                loadingText="Generating..."
                disabled={isReportLoading}
              >
                Download Report
              </Button>
             {canReport && (
                <Button
                  onClick={handleDownloadProductionSheetMainReport}
                  className="px-4 py-2 text-white bg-[#EB1C37] rounded-lg hover:bg-[#EB1C37]/90"
                  variant="default"
                  loading={productionSheetMainReportDownloadLoading}
                  loadingText="Generating..."
                  disabled={!hasProductionSheetSignatures() || productionSheetMainReportDownloadLoading || hasUnsavedChanges}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Generate Report
                </Button>
              )} 
              <Button
                onClick={handleSave}
                className="px-4 py-2 bg-[#EB1C37] text-white rounded-lg hover:bg-[#EB1C37]/90"
                loading={
                  currentView === 'main' ? statusUpdateLoading :
                    currentView === 'fitment' ? fitmentUpdateLoading :
                      currentView === 'visual' ? visualInspectionUpdateLoading :
                        currentView === 'alignment' ? alignmentUpdateLoading : false
                }
                disabled={
                  !canEdit || !hasUnsavedChanges
                }
                loadingText="Saving..."
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </div>
        {productionDataLoading ? (
          <div className="flex items-center justify-center min-h-[700px]">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
              <span className="text-gray-600 text-lg">Loading production data...</span>
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="p-6 space-y-6">
              {/* Production Details */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium">Production Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                  {/* Documentation and Basic Info */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">WPS Number</Label>
                    <Select value={selectedWpsId || ''} onValueChange={handleWpsChange} disabled={!canEdit || !isProductionSheetEditMode}>
                      <SelectTrigger className="w-full" disabled={!canEdit || !isProductionSheetEditMode}>
                        <SelectValue placeholder="Select WPS" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Clear Selection</SelectItem>
                        {wpsOptions.length > 0 ? (
                          wpsOptions.map(wps => (
                            <SelectItem key={wps.value} value={wps.value}>
                              {wps.label.length > 30
                                ? `${wps.label.substring(0, 30)}...`
                                : wps.label}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-wps" disabled>
                            No WPS specifications found
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Drawing Number</Label>
                    <Select value={selectedDrawingId || ''} onValueChange={handleDrawingChange} disabled={!canEdit || !isProductionSheetEditMode}>
                      <SelectTrigger className="w-full" disabled={!canEdit || !isProductionSheetEditMode}>
                        <SelectValue placeholder="Select drawing" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Clear Selection</SelectItem>
                        {drawingOptions.length > 0 ? (
                          drawingOptions
                            .map(drawing => (
                              <SelectItem key={drawing.value} value={drawing.value}>
                                {drawing.label.length > 30
                                  ? `${drawing.label.substring(0, 30)}...`
                                  : drawing.label}
                              </SelectItem>
                            ))
                        ) : (
                          <SelectItem value="no-drawings" disabled>
                            No drawings found.
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Gas Serial Numbers</Label>
                    <div className="w-full">
                      <div className={!canEdit || !isProductionSheetEditMode ? "pointer-events-none opacity-50" : ""}>
                        <MultiSelect
                          options={gasOptions}
                          selected={selectedGasIds}
                          onChange={handleGasChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Wire Lot Numbers</Label>
                    <div className="w-full">
                      <div className={!canEdit || !isProductionSheetEditMode ? "pointer-events-none opacity-50" : ""}>
                        <MultiSelect
                          options={wireLotOptions}
                          selected={selectedWireIds}
                          onChange={handleWireChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Physical Components and Measurements */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Forge NRI</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Select
                          value={selectedForgeId || ''}
                          onValueChange={handleForgeChange}
                          disabled={!canEdit || !isProductionSheetEditMode}
                          open={isForgeSelectOpen}
                          onOpenChange={setIsForgeSelectOpen}
                        >
                          <SelectTrigger className="w-full" disabled={!canEdit || !isProductionSheetEditMode}>
                            <SelectValue placeholder="Select forge" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {/* Search Input */}
                            <div className="p-2 border-b border-gray-200 flex-shrink-0">
                              <Input
                                placeholder="Search forge..."
                                value={forgeSearchTerm}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setForgeSearchTerm(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Escape') {
                                    setIsForgeSelectOpen(false);
                                    setForgeSearchTerm('');
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="h-8 text-sm"
                                autoFocus
                              />
                            </div>
                            {/* Scrollable Options List */}
                            <div className="overflow-y-auto max-h-[240px]">
                              <SelectItem value="none">
                                Clear Selection
                              </SelectItem>
                              {forgeOptions.length > 0 ? (
                                forgeOptions
                                  .filter(forge => {
                                    if (!forgeSearchTerm) return true;
                                    return forge.label.toLowerCase().includes(forgeSearchTerm.toLowerCase()) ||
                                      forge.forgeNRI?.toLowerCase().includes(forgeSearchTerm.toLowerCase());
                                  })
                                  .map(forge => (
                                    <SelectItem
                                      key={forge.value}
                                      value={forge.value}
                                    >
                                      {forge.label}
                                    </SelectItem>
                                  ))
                              ) : (
                                <SelectItem value="no-forge" disabled>
                                  No forge materials available
                                </SelectItem>
                              )}
                            </div>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0 h-10 w-10"
                        onClick={() => handleQRScan('forge')}
                      >
                        <QrCode className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Pipe NRI</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <SearchableSelectDropdown
                          value={selectedPipeId ?? 'none'}
                          onValueChange={handlePipeChange}
                          options={pipeOptions}
                          placeholder="Select pipe"
                          searchPlaceholder="Search pipe..."
                          noneOption={{ value: 'none', label: 'Clear Selection' }}
                          filterKeys={['label', 'pipeNRI']}
                          disabled={!canEdit || !isProductionSheetEditMode}
                          open={isPipeSelectOpen}
                          onOpenChange={setIsPipeSelectOpen}
                          emptyMessage="No pipe materials available"
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0 h-10 w-10"
                        onClick={() => handleQRScan('pipe')}
                      >
                        <QrCode className="h-4 w-4" />
                      </Button>
                    </div>
                  </div> */}

                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Distance from Pipe End to Forge ({getUnitLabel(unit)})</Label>
                    <Input
                      type="number"
                      placeholder={`Enter distance in ${getUnitLabel(unit)}`}
                      min="0"
                      step="1"
                      value={distanceFromPipeEnd || ''}
                      onChange={handleDistanceChange}
                      className="w-full"
                      disabled={!canEdit || !isProductionSheetEditMode}
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Status</Label>
                    <Select value={productionStatus || ''} onValueChange={handleStatusChange} disabled={!canEdit || !isProductionSheetEditMode}>
                      <SelectTrigger className="w-full" disabled={!canEdit || !isProductionSheetEditMode}>
                        <SelectValue placeholder="Select status..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Not Started">Not Started</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="On Hold">On Hold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Start Date</Label>
                    <AntdDate
                      name="startDate"
                      value={formData.startDate}
                      onChange={(value: string) => {
                        markAsUnsaved();
                        setFormData(prev => ({ ...prev, startDate: value }));

                      }}
                      disabled={!canEdit || !isProductionSheetEditMode}
                    />
                  </div>

                  <div className="space-y-3">
                    <Label >End Date</Label>
                    <AntdDate
                      name="endDate"
                      value={formData.endDate}
                      disabled={!canEdit || !isProductionSheetEditMode || !formData.startDate}
                      minDate={formData.startDate ? dayjs(formData.startDate) : undefined}
                      onChange={(value: string) => {
                        // Validate that end date is after start date
                        if (formData.startDate && value) {
                          const startDate = new Date(formData.startDate);
                          const endDate = new Date(value);

                          if (endDate <= startDate) {
                            Notification.error('End date must be after start date');
                            return;
                          }
                        }
                        markAsUnsaved();
                        setFormData(prev => ({ ...prev, endDate: value }));

                      }}
                    />
                  </div>

                  {/* <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Pass Configuration</Label>
                    <div className="flex gap-2">
                      <Select
                        value={selectedPassConfigurationId || ''}
                        onValueChange={handlePassConfigurationChange}
                        disabled={isPassConfigurationSaved}
                      >
                        <SelectTrigger className={`w-full ${isPassConfigurationSaved ? "bg-gray-100 cursor-not-allowed" : ""}`}>
                          <SelectValue placeholder={"Select Pass Configuration"} />
                        </SelectTrigger>
                        <SelectContent>
                          {passConfigurationOptions.length > 0 ? (
                            passConfigurationOptions.map(pass => (
                              <SelectItem key={pass.value} value={pass.value}>
                                {pass.label}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no-config" disabled>
                              No Pass Configuration specifications found
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      
                    </div>
                    {isPassConfigurationSaved && (
                      <p className="text-xs text-gray-500 mt-1">
                        Pass configuration is already saved and cannot be changed
                      </p>
                    )}
                    
                  </div> */}
                </div>
              </div>

              {/* Assembly Checks */}
              {/* <div className="space-y-4">
              <h3 className="text-lg font-medium">Assembly Checks</h3>
              <div className="flex items-center gap-8">
                <label className="flex items-center gap-2">
                  <Checkbox />
                  <span className="text-sm font-medium">Fitment</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox />
                  <span className="text-sm font-medium">Visual Inspection</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox />
                  <span className="text-sm font-medium">NDT</span>
                </label>
              </div>
            </div> */}

              {/* Weld and Inspection Traceability */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium flex items-center justify-between">
                  Weld and Inspection Traceability
                  <div className="flex items-center gap-2">
                    {(productionSheetData && productionSheetData?.productionSheetId) ? <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        setIsRepairAlignmentView(false);
                        setSelectedRepairIndex(null);
                        setCurrentView('alignment');
                        setSelectedWeld(0);
                        await UpdateAlignmentData(productionSheetData);

                      }}
                      className="flex items-center gap-2 text-xs"
                    >
                      <Checkbox disabled
                        checked={productionSheetData?.productionAlignment?.qcInspectorId ? true : false}


                      />
                      Alignment
                    </Button>
                      :
                      <Tooltip content="Save production details to create alignment" placement="top">
                        <Button size="sm" variant="outline" className="flex items-center gap-2 text-xs" disabled>
                          <Checkbox disabled />
                          Alignment
                        </Button>
                      </Tooltip>
                    }
                    {(() => {
                      // Get unique repair indices from both weld 1 and weld 2
                      const maxRepairs = Math.max(numberOfRepairsWeld1 || 0, numberOfRepairsWeld2 || 0);
                      const uniqueRepairIndices = Array.from({ length: maxRepairs }, (_, i) => i + 1);

                      return uniqueRepairIndices.map((repairindex) => {
                        // Check if repair weld data exists for either weld 1 or weld 2
                        const repairWeldData = productionSheetData?.productionWelds?.find(
                          (w: any) => w.repairindex === repairindex
                        );
                        // Repair alignment is common for both welds, find by repairindex only
                        const repairAlignment = productionSheetData?.productionRepairAlignments?.find(
                          (ra: any) => ra.repairindex === repairindex
                        );
                        // Check if both QC and customer signatures exist
                        const hasBothSignatures = !!(
                          repairAlignment?.signature?.qc?.signature &&
                          repairAlignment?.signature?.customer?.signature
                        );

                        return (
                          <div key={`repair-${repairindex}`}>
                            {repairWeldData ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  setIsRepairAlignmentView(true);
                                  setSelectedRepairIndex(repairindex);
                                  // Use weldindex 1 as default, but alignment is common for both welds
                                  await UpdateRepairAlignmentData(1, repairindex);
                                  setCurrentView('alignment');
                                  setSelectedWeld(0);
                                }}
                                disabled={!repairWeldData}
                                className="flex items-center gap-2 text-xs"
                              >
                                <Checkbox checked={hasBothSignatures} disabled />
                                Repair {repairindex} Alignment
                              </Button>
                            ) : (
                              <Tooltip content="Save production details to create alignment" placement="top">
                                <Button size="sm" variant="outline" className="flex items-center gap-2 text-xs" disabled>
                                  <Checkbox disabled />
                                  Repair {repairindex} Alignment
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        );
                      });
                    })()}


                  </div>
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Weld 1 */}
                  <div className="border rounded-lg p-6 space-y-6 bg-white shadow-sm">
                    <>

                      <div className="flex items-center gap-3 flex-wrap">
                        {/* Header row with weld name and identifier */}
                        <div className="flex flex-col items-center justify-center min-w-[60px]">
                          <h4 className="text-sm font-semibold text-blue-800">Weld 1</h4>




                          {/* <span className="text-sm font-semibold text-blue-800">1</span> */}
                        </div>
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            id="weldName1"
                            value={weld1Name || ''}
                            onChange={(e) => {
                              setweld1Name(e.target.value)
                              markAsUnsaved()
                            }
                            }
                            placeholder="Enter Weld Name"
                            className="w-full max-w-[200px]"
                            disabled={!canEdit || !isProductionSheetEditMode}
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            className="shrink-0 h-10 w-10"
                            onClick={() => handleQRScan('weld1')}
                            disabled={!canEdit || !isProductionSheetEditMode}
                          >
                            <QrCode className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="outline"
                            className="shrink-0 h-10 w-10"
                            onClick={() => { setWeldSectionDialogWeldIndex(1); setWeldSectionDialogRepairIndex(0); }}
                            title="Weld section: Pipe NRI, Pass config, Fitment & Visual"
                          //  disabled={!productionSheetData || !productionSheetData?.productionWelds || productionSheetData?.productionWelds?.length === 0}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Buttons row */}
                        {productionSheetData && productionSheetData?.productionWelds && productionSheetData?.productionWelds?.length > 0 ? (
                          <div className="flex items-center gap-2">


                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                await UpdateFitmentData(productionSheetData.productionWelds[0], 0);
                                setCurrentView('fitment');
                                setSelectedWeld(0);
                              }}
                              disabled={!productionSheetData || !productionSheetData?.productionWelds || productionSheetData?.productionWelds?.length == 0}
                              className="flex items-center gap-2 text-xs"
                            >
                              <Checkbox checked={!!productionSheetData?.productionWelds[0]?.weldFitment?.qcInspectorId} disabled />
                              Fitment
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                await UpdateVisualInspectionData(productionSheetData.productionWelds[0]);
                                setCurrentView('visual');
                                setSelectedWeld(0);
                              }}
                              disabled={!productionSheetData || !productionSheetData?.productionWelds || productionSheetData?.productionWelds?.length == 0}
                              className="flex items-center gap-2 text-xs"
                            >
                              <Checkbox checked={!!productionSheetData.productionWelds[0]?.weldVisualInspection?.qcInspectorId} disabled />
                              Visual Inspection
                            </Button>


                            {/* <Button
                             size="sm"
                             variant="outline"
                             onClick={async () => {
                               await UpdateAlignmentData(productionSheetData.productionWelds[0]);
                               setCurrentView('alignment');
                               setSelectedWeld(0);
                             }}
                             className="flex items-center gap-2 text-xs"
                           >
                             <Checkbox checked={!!productionSheetData.productionWelds[0]?.weldAlignment.qcInspectorId} disabled   />
                             Alignment
                             </Button> */}
                          </div>
                        )


                          :
                          (
                            <div className="flex items-center gap-2">
                              <Tooltip content="Save production details to create fitment" placement="top">
                                <Button size="sm" variant="outline" className="flex items-center gap-2 text-xs " disabled>
                                  <Checkbox disabled />
                                  Fitment
                                </Button>
                              </Tooltip>
                              <Tooltip content="Save production details to create visual inspection" placement="top">
                                <Button size="sm" variant="outline" className="flex items-center gap-2 text-xs" disabled>
                                  <Checkbox disabled />
                                  Visual Inspection
                                </Button>
                              </Tooltip>

                            </div>
                          )
                        }



                      </div>

                      {/* Pills: Pipe NRI, Pass Configuration, Fitment for Weld 1 */}
                      {productionSheetData?.productionSheetId && <div className="flex flex-wrap gap-2 mt-2">
                        {(() => {
                          const pipeLabel = weld1PipeId ? (pipeOptions.find((o: any) => o.value === weld1PipeId)?.label ?? '-') : 'None';
                          const passLabel = weld1PassConfigId ? (passConfigurationOptions.find((o: any) => String(o.value) === String(weld1PassConfigId))?.label ?? 'Pass') : '—';
                          const fitLabel = weld1FitmentId ? (fitmentOptions.find((o: any) => String(o.value) === String(weld1FitmentId))?.label ?? 'Fitment') : 'None';
                          const items = [
                            { label: 'Pipe NRI', value: pipeLabel, bgColor: 'bg-slate-100 text-slate-700' },
                            { label: 'Pass Configuration', value: passLabel, bgColor: 'bg-amber-50 text-amber-800' },
                            { label: 'Fitment', value: fitLabel, bgColor: 'bg-emerald-50 text-emerald-800' },
                          ];
                          return items.map((item, index) => (
                            <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                              {item.label}: {item.value}
                            </span>
                          ));
                        })()}
                      </div>}

                      {/* Welder 1Selection */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium text-gray-700">Welder</Label>
                        <div className="w-full">
                          <div className={!canEdit || !isProductionSheetEditMode ? "pointer-events-none opacity-50" : ""}>
                            <MultiSelect
                              options={welderList}
                              selected={selectedWelder1}
                              onChange={handleWelder1Change}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Passes */}
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-12 items-center border-b border-gray-200 pb-3 gap-2">
                          <span className="text-sm font-medium sm:col-span-4">Passes</span>
                          <span className="text-sm font-medium sm:col-span-2 text-center">Status</span>
                          <span className="text-sm font-medium sm:col-span-6 text-center">Pre heat or interpass temperature (°C)</span>
                          {/* <span className="text-sm font-medium sm:col-span-5 text-center">Welder</span> */}
                        </div>
                        {passConfigurationWeld_1.length > 0 ? passConfigurationWeld_1.map((pass: any, index: number) => (
                          <div key={index} className="grid grid-cols-1 sm:grid-cols-12 items-center border-b border-gray-200 pb-4 gap-2">
                            <span className="text-sm font-medium sm:col-span-4">{pass.passName}</span>
                            <div className="sm:col-span-2 flex justify-center">
                              <Checkbox checked={pass.passStatus}
                                onCheckedChange={(checked) => handlePassStatusChange_weld_1(checked, index)}
                                disabled={!canEdit || !isProductionSheetEditMode} />
                            </div>
                            <div className="sm:col-span-6 flex justify-center">
                              <Input
                                type="number"
                                placeholder="Temperature"
                                className="w-full max-w-48"
                                value={pass.passTemperature}
                                onChange={(e) => handlePassTemperatureChange_weld_1(e, index)}
                                disabled={!canEdit || !isProductionSheetEditMode}
                              />
                            </div>
                            {/* <div className="sm:col-span-5 flex justify-center">
                              <div className={!canEdit || !isProductionSheetEditMode ? "pointer-events-none opacity-50 w-full" : "w-full"}>
                                <MultiSelect
                                  options={welderList}
                                  selected={pass.passWelder || []}
                                  onChange={(selected) => handlePassWelderChange_weld_1(selected, index)}
                                />
                              </div>
                            </div> */}
                          </div>
                        )) : (
                          <div className="grid grid-cols-1 sm:grid-cols-12 items-center border-b border-gray-200 pb-4 gap-2">
                            <h3 className="text-lg font-medium text-gray-900 mb-2 sm:col-span-12 text-center">No passes created yet</h3>
                            <p className="text-gray-500 max-w-sm sm:col-span-12 text-center mx-auto">
                              You need to create passes in the specifications. Go to specification → add new pass configuration.
                            </p>
                          </div>
                        )}
                      </div>
                    </>


                    {/* Repair welds for Weld 1 */}
                    {Array.from({ length: numberOfRepairsWeld1 }).map((_, index) => {
                      const repairindex = index + 1;
                      return (
                        <div key={`repair-1-${repairindex}`} className="mt-6 border-t pt-6">
                          {getWeldData(1, repairindex)}
                        </div>
                      );
                    })}
                    {productionSheetData?.productionSheetId && <div className="flex justify-end">
                      <Button size="sm" className="flex items-center gap-2 text-xs"
                        onClick={() => {
                          const newRepairNumber = numberOfRepairsWeld1 + 1;
                          setNumberOfRepairsWeld1(newRepairNumber);
                          // Initialize repair weld data
                          const key = getRepairWeldKey(1, newRepairNumber);
                          const basePasses = passConfigurationWeld_1.map((pass: any) => ({
                            ...pass,
                            passStatus: false,
                            passTemperature: null,
                            passWelder: []
                          }));
                          setRepairWelds(new Map(repairWelds.set(key, {
                            weldName: '',
                            passConfiguration: basePasses
                          })));
                          markAsUnsaved();
                        }}
                        disabled={!canEdit || !isProductionSheetEditMode}
                      >
                        <Plus className="h-4 w-4" />
                        Add Repair
                      </Button>
                    </div>}
                  </div>

                  {/* Weld 2 */}
                  <div className="border rounded-lg p-6 space-y-6 bg-white shadow-sm">
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Header row with weld name and identifier */}
                      <div className="flex flex-col items-center justify-center min-w-[60px]">
                        <h4 className="text-sm font-semibold text-blue-800">Weld 2</h4>
                        {/* <span className="text-sm font-semibold text-blue-800">2</span> */}
                      </div>
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          id="weldName2"
                          value={weld2Name || ''}
                          onChange={(e) => {
                            setweld2Name(e.target.value)
                            markAsUnsaved()
                          }
                          }
                          placeholder="Enter Weld Name"
                          className="w-full max-w-[200px]"
                          disabled={!canEdit || !isProductionSheetEditMode}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="shrink-0 h-10 w-10"
                          onClick={() => handleQRScan('weld2')}
                          disabled={!canEdit || !isProductionSheetEditMode}
                        >
                          <QrCode className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => { setWeldSectionDialogWeldIndex(2); setWeldSectionDialogRepairIndex(0); }}
                          title="Weld section: Pipe NRI, Pass config, Fitment & Visual"
                        //  disabled={!productionSheetData || !productionSheetData?.productionWelds || productionSheetData?.productionWelds?.length < 2}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Buttons row */}
                      {productionSheetData && productionSheetData.productionWelds && productionSheetData.productionWelds.length > 1 ? (
                        <div className="flex items-center gap-2">

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              await UpdateFitmentData(productionSheetData.productionWelds[1], 1);
                              setSelectedWeld(1);
                              setCurrentView('fitment');
                            }}
                            className="flex items-center gap-2 text-xs"
                          >
                            <Checkbox checked={!!productionSheetData?.productionWelds[1]?.weldFitment?.qcInspectorId} disabled />

                            Fitment
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              await UpdateVisualInspectionData(productionSheetData?.productionWelds[1]);
                              setCurrentView('visual');
                              setSelectedWeld(1);
                            }}
                            className="flex items-center gap-2 text-xs"
                          >
                            <Checkbox checked={!!productionSheetData.productionWelds[1]?.weldVisualInspection?.qcInspectorId} disabled />
                            Visual Inspection
                          </Button>

                          {/* <Button
                             size="sm"
                             variant="outline"
                             onClick={async () => {
                               await UpdateAlignmentData(productionSheetData.productionWelds[1]);
                               setCurrentView('alignment');
                               setSelectedWeld(1);
                             }}
                             className="flex items-center gap-2 text-xs"
                           >
                             <Checkbox checked={!!productionSheetData.productionWelds[1]?.weldAlignment.qcInspectorId} disabled />
                             Alignment
                             </Button> */}
                        </div>
                      )
                        :
                        (
                          <div className="flex items-center gap-2">
                            <Tooltip content="Save production details to create fitment" placement="top">
                              <Button size="sm" variant="outline" className="flex items-center gap-2 text-xs" disabled
                              >
                                <Checkbox disabled />
                                Fitment
                              </Button>
                            </Tooltip>
                            <Tooltip content="Save production details to create visual inspection" placement="top">
                              <Button size="sm" variant="outline" className="flex items-center gap-2 text-xs" disabled>
                                <Checkbox disabled />
                                Visual Inspection
                              </Button>
                            </Tooltip>
                          </div>
                        )}

                    </div>

                    {/* Pills: Pipe NRI, Pass Configuration, Fitment for Weld 2 */}
                    {productionSheetData?.productionSheetId && <div className="flex flex-wrap gap-2 mt-2">
                      {(() => {
                        const pipeLabel = weld2PipeId ? (pipeOptions.find((o: any) => o.value === weld2PipeId)?.label ?? '-') : 'None';
                        const passLabel = weld2PassConfigId ? (passConfigurationOptions.find((o: any) => String(o.value) === String(weld2PassConfigId))?.label ?? 'Pass') : '—';
                        const fitLabel = weld2FitmentId ? (fitmentOptions.find((o: any) => String(o.value) === String(weld2FitmentId))?.label ?? 'Fitment') : 'None';
                        const items = [
                          { label: 'Pipe NRI', value: pipeLabel, bgColor: 'bg-slate-100 text-slate-700' },
                          { label: 'Pass Configuration', value: passLabel, bgColor: 'bg-amber-50 text-amber-800' },
                          { label: 'Fitment', value: fitLabel, bgColor: 'bg-emerald-50 text-emerald-800' },
                        ];
                        return items.map((item, index) => (
                          <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                            {item.label}: {item.value}
                          </span>
                        ));
                      })()}
                    </div>}

                    {/* Welder 2 Selection */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium text-gray-700">Welder</Label>
                      <div className="w-full">
                        <div className={!canEdit || !isProductionSheetEditMode ? "pointer-events-none opacity-50" : ""}>
                          <MultiSelect
                            options={welderList}
                            selected={selectedWelder2}
                            onChange={handleWelder2Change}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Passes */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-12 items-center border-b border-gray-200 pb-3 gap-2">
                        <span className="text-sm font-medium sm:col-span-4">Passes</span>
                        <span className="text-sm font-medium sm:col-span-2 text-center">Status</span>
                        <span className="text-sm font-medium sm:col-span-6 text-center">Pre heat or interpass temperature (°C)</span>
                        {/* <span className="text-sm font-medium sm:col-span-5 text-center">Welder</span> */}
                      </div>
                      {passConfigurationWeld_2.length > 0 ? passConfigurationWeld_2.map((pass: any, index: number) => (
                        <div key={index} className="grid grid-cols-1 sm:grid-cols-12 items-center border-b border-gray-200 pb-4 gap-2">
                          <span className="text-sm font-medium sm:col-span-4">{pass.passName}</span>
                          <div className="sm:col-span-2 flex justify-center">
                            <Checkbox checked={pass.passStatus} onCheckedChange={(checked) => handlePassStatusChange_weld_2(checked, index)} disabled={!canEdit || !isProductionSheetEditMode} />
                          </div>
                          <div className="sm:col-span-6 flex justify-center">
                            <Input
                              type="number"
                              placeholder="Temperature"
                              className="w-full max-w-48"
                              value={pass.passTemperature}
                              onChange={(e) => handlePassTemperatureChange_weld_2(e, index)}
                              disabled={!canEdit || !isProductionSheetEditMode}
                            />
                          </div>
                          {/* <div className="sm:col-span-5 flex justify-center">
                            <div className={!canEdit || !isProductionSheetEditMode ? "pointer-events-none opacity-50 w-full" : "w-full"}>
                              <MultiSelect
                                options={welderList}
                                selected={pass.passWelder || []}
                                onChange={(selected) => handlePassWelderChange_weld_2(selected, index)}
                              />
                            </div>
                          </div> */}
                        </div>
                      )) : (
                        <div className="grid grid-cols-1 sm:grid-cols-12 items-center border-b border-gray-200 pb-4 gap-2">
                          <h3 className="text-lg font-medium text-gray-900 mb-2 sm:col-span-12 text-center">No passes created yet</h3>
                          <p className="text-gray-500 max-w-sm sm:col-span-12 text-center mx-auto">
                            You need to create passes in the specifications. Go to specification → add new pass configuration.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Repair welds for Weld 2 */}
                    {Array.from({ length: numberOfRepairsWeld2 }).map((_, index) => {
                      const repairindex = index + 1;
                      return (
                        <div key={`repair-2-${repairindex}`} className="mt-6 border-t pt-6">
                          {getWeldData(2, repairindex)}
                        </div>
                      );
                    })}
                    <div className="flex justify-end">
                      <Button size="sm" className="flex items-center gap-2 text-xs"
                        onClick={() => {
                          const newRepairNumber = numberOfRepairsWeld2 + 1;
                          setNumberOfRepairsWeld2(newRepairNumber);
                          // Initialize repair weld data
                          const key = getRepairWeldKey(2, newRepairNumber);
                          const basePasses = passConfigurationWeld_2.map((pass: any) => ({
                            ...pass,
                            passStatus: false,
                            passTemperature: null,
                            passWelder: []
                          }));
                          setRepairWelds(new Map(repairWelds.set(key, {
                            weldName: '',
                            passConfiguration: basePasses
                          })));
                          markAsUnsaved();
                        }}
                        disabled={!canEdit || !isProductionSheetEditMode}
                      >
                        <Plus className="h-4 w-4" />
                        Add Repair
                      </Button>
                    </div>
                  </div>
                </div>
              </div>



              <Signature
                customerSignature={selectedCustomerRepresentativeId}
                qcSignature={selectedQcInspectorId}
                customerRepresentativeList={customerRepresentativeList}
                qcList={qcList}
                handleQcSignatureChange={handleQcSignatureChange}
                handleCustomerSignatureChange={handleCustomerSignatureChange}
                signatureData={productionSheetData}
                onSignatureChange={OnHandleSIngaure}
                canSign={canSign}
                canEdit={canEdit}
                isEditMode={isProductionSheetEditMode}
                isCustomerRepresentativeUser={isCustomerRepresentativeUser}
                isQCInspectorUser={isQCInspectorUser}
                isWelderUser={isWelderUser}
                currentUserTeamMemberId={currentUserTeamMemberId}
                isAdminOrSuperAdmin={isAdminOrSuperAdmin}
              />


            </CardContent>
          </Card>
        )}
        {/* Unsaved Changes Confirmation Dialog */}
        <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
          <DialogContent className="max-w-[700px] p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <DialogTitle className="text-xl font-semibold">
                Unsaved Changes
              </DialogTitle>
            </div>
            <div>
              <p className="text-gray-700 mb-3">
                You have unsaved changes in the Production tab. What would you like to do?
              </p>
              <div className="mt-4 p-3 bg-yellow-50 border rounded-md">
                <p className="text-sm text-yellow-700">
                  <span className="font-semibold">Note:</span> Choose "Save" to keep your changes, "Discard" to remove changes and close, or "Cancel" to stay on this page.
                </p>
              </div>
            </div>
            <DialogFooter className="gap-3">
              <Button
                variant="outline"
                onClick={handleCancelNavigation}
                className="min-w-[100px]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAndClose}
                className="min-w-[100px]"
                loading={
                  statusUpdateLoading
                }
                loadingText="Saving..."
                disabled={!hasUnsavedChanges && (statusUpdateLoading)}
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>


              <Button
                onClick={handleConfirmNavigation}
                className="min-w-[100px] bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Discard
                </div>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Weld Section Dialog: Pipe NRI, Pass Configuration, Fitment per weld */}
        <Dialog
          open={weldSectionDialogWeldIndex !== null}
          onOpenChange={(open) => { if (!open) { setWeldSectionDialogWeldIndex(null); setWeldSectionDialogRepairIndex(0); } }}
        >
          <DialogContent className="max-w-2xl w-[120vw] sm:w-[900px] p-6">
            <div className="flex items-center gap-3 pb-2 border-b">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { setWeldSectionDialogWeldIndex(null); setWeldSectionDialogRepairIndex(0); }}
                className="shrink-0 -ml-1"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <DialogTitle className="text-lg font-semibold flex-1">
                {weldSectionDialogRepairIndex === 0
                  ? `Weld ${weldSectionDialogWeldIndex} Section`
                  : `Weld ${weldSectionDialogWeldIndex} Repair ${weldSectionDialogRepairIndex} Section`}
              </DialogTitle>
            </div>
            {weldSectionDialogWeldIndex !== null && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Pipe NRI</Label>
                  <SearchableSelectDropdown
                    value={weldSectionPendingPipeId ?? '__none__'}
                    onValueChange={(v) => {
                      const newPipeId = v === '__none__' ? null : v;
                      if (newPipeId != null && pipeOptions.length > 0) {
                        const selectedOption = pipeOptions.find((option: any) => String(option.value) === String(newPipeId));
                        if (selectedOption?.status === 'Consumed') {
                          Notification.warning('Selected pipe is already consumed', 'Please select an available pipe');
                          return;
                        }
                      }
                      setWeldSectionPendingPipeId(newPipeId);
                    }}

                    options={pipeOptions}
                    placeholder="Select Pipe NRI"
                    searchPlaceholder="Search pipe..."
                    noneOption={{ value: '__none__', label: 'None' }}
                    filterKeys={['label', 'pipeNRI']}
                    disabled={!canEdit || !isProductionSheetEditMode}
                    uncontrolled
                    emptyMessage="No pipe materials available"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Pass Configuration</Label>
                  <SearchableSelectDropdown
                    value={weldSectionPendingPassConfigId ?? ''}
                    onValueChange={(v) => setWeldSectionPendingPassConfigId(v || null)}
                    options={passConfigurationOptions.map((opt: any) => ({ value: String(opt.value), label: opt.label, ...opt }))}
                    placeholder="Select Pass Configuration"
                    searchPlaceholder="Search pass configuration..."
                    filterKeys={['label']}
                    disabled={!canEdit || !isProductionSheetEditMode}
                    uncontrolled
                    emptyMessage="No Pass Configuration found"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-sm font-medium text-gray-700">Fitment</Label>
                  <SearchableSelectDropdown
                    value={weldSectionPendingFitmentId ?? '__none__'}
                    onValueChange={(v) => setWeldSectionPendingFitmentId(v === '__none__' ? null : v)}
                    options={fitmentOptions}
                    placeholder="Select Fitment (from specification)"
                    searchPlaceholder="Search fitment..."
                    noneOption={{ value: '__none__', label: 'None' }}
                    filterKeys={['label']}
                    disabled={!canEdit || !isProductionSheetEditMode}
                    uncontrolled
                    emptyMessage="No Fitment specification found"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setWeldSectionDialogWeldIndex(null); setWeldSectionDialogRepairIndex(0); }}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  const repairIdx = weldSectionDialogRepairIndex ?? 0;
                  if (repairIdx === 0) {
                    if (weldSectionDialogWeldIndex === 1) {
                      setWeld1PipeId(weldSectionPendingPipeId);
                      setWeld1PassConfigId(weldSectionPendingPassConfigId);
                      setWeld1FitmentId(weldSectionPendingFitmentId);
                      if (weldSectionPendingPassConfigId) applyPassConfigurationForWeld(1, weldSectionPendingPassConfigId);
                    } else if (weldSectionDialogWeldIndex === 2) {
                      setWeld2PipeId(weldSectionPendingPipeId);
                      setWeld2PassConfigId(weldSectionPendingPassConfigId);
                      setWeld2FitmentId(weldSectionPendingFitmentId);
                      if (weldSectionPendingPassConfigId) applyPassConfigurationForWeld(2, weldSectionPendingPassConfigId);
                    }
                  } else if (weldSectionDialogWeldIndex != null) {
                    const key = getRepairWeldKey(weldSectionDialogWeldIndex, repairIdx);
                    const current = repairWelds.get(key) || { weldName: '', passConfiguration: [] };
                    setRepairWelds(new Map(repairWelds.set(key, {
                      ...current,
                      materialPipeId: weldSectionPendingPipeId ?? undefined,
                      passConfigDocId: weldSectionPendingPassConfigId ?? undefined,
                      fitmentSpecDocId: weldSectionPendingFitmentId ?? undefined,
                    })));
                    if (weldSectionPendingPassConfigId) applyPassConfigurationForWeld(weldSectionDialogWeldIndex, weldSectionPendingPassConfigId, repairIdx);
                  }
                  markAsUnsaved();
                  setWeldSectionDialogWeldIndex(null);
                  setWeldSectionDialogRepairIndex(0);
                }}
              >
                Apply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* QR Scanner Dialog */}
        {isQRScannerOpen && <Dialog open={isQRScannerOpen} onOpenChange={setIsQRScannerOpen}>
          <DialogContent className="max-w-2xl w-[50vw]">
            <DialogTitle className="text-lg font-semibold mb-4">
              Scan QR Code
            </DialogTitle>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {scanningFor === 'forge' && 'Scan forge QR code to select forge material'}
                {scanningFor === 'pipe' && 'Scan pipe QR code to select pipe material'}
                {scanningFor === 'weld1' && 'Scan QR code to set Weld 1 name'}
                {scanningFor === 'weld2' && 'Scan QR code to set Weld 2 name'}
                {scanningFor && scanningFor.startsWith('repair-weld-') && (() => {
                  const parts = scanningFor.split('-');
                  if (parts.length === 4) {
                    const weldindex = parseInt(parts[2], 10);
                    const repairindex = parseInt(parts[3], 10);
                    return `Scan QR code to set Repair ${repairindex} (Weld ${weldindex}) name`;
                  }
                  return 'Scan QR code to set repair weld name';
                })()}
              </p>
              {isQRScannerOpen && (
                <QRScanner
                  onResult={handleQRResult}
                  onError={(error) => {
                    console.error('QR Scanner Error:', error);
                    Notification.error(`QR Scanner Error: ${error}`);
                  }}
                  isOpen={isQRScannerOpen}
                />
              )}
              <div className="flex justify-end mt-4">
                <Button
                  onClick={() => {
                    console.log('Closing QR scanner dialog');
                    setIsQRScannerOpen(false);
                    setScanningFor(null);
                  }}
                  variant="outline"
                  className="px-4 py-2"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>}

        {/* Edit Confirmation Dialog - Shows when Edit button is clicked */}
        <Dialog open={showEditConfirmation} onOpenChange={setShowEditConfirmation}>
          <DialogContent className="max-w-[700px] p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <DialogTitle className="text-xl font-semibold">
                Edit Mode Confirmation
              </DialogTitle>
            </div>
            <div>
              <p className="text-gray-700 mb-3">
                If you make any changes, please add signatures after editing.
              </p>
              <div className="mt-4 p-3 bg-yellow-50 border rounded-md">
                <p className="text-sm text-yellow-700">
                  <span className="font-semibold">Note:</span> After editing, you will need to add QC and Customer signatures before saving. Click "Yes" to proceed to edit mode, or "No" to stay in view mode.
                </p>
              </div>
            </div>
            <DialogFooter className="gap-3">
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                className="min-w-[100px]"
              >
                No
              </Button>
              <Button
                variant="default"
                onClick={handleConfirmProductionSheetEdit}
                className="min-w-[100px] bg-[#EB1C37] hover:bg-[#EB1C37]/90 text-white"
              >
                Yes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Document Approval Modal */}
        {isDocumentApprovalModalOpen && productionSheetData && (
          <DocumentApprovalModal
            isOpen={isDocumentApprovalModalOpen}
            onClose={() => setIsDocumentApprovalModalOpen(false)}
            onOpenViewer={openViewer}
            productionSheetData={productionSheetData}
            selectedPipe={selectedPipe}
            assemblyNumber={selectedItem?.assemblyNumber}
            onDownloadMergedReport={async () => { }}
            onStatusChange={() => { }}
            onRefresh={loadProductionData}
            isFitmentreport={false}
            isVisualInspectionreport={false}
            isAlignmentreport={false}
            isProductionSheetreport={true}
            isNDTreport={false}
            isCoatingreport={false}
            isMergeProductionReport={false}
            isMergeReport={false}
            isVissibleApprovalButton={false}
          />
        )}


        {/* Delete Repair Weld Confirmation Dialog */}
        {itemToDeleteRepairWeld && (
          <DeleteConfirmationDialog
            isOpen={isDeleteRepairWeldModalVisible}
            onClose={() => {
              setIsDeleteRepairWeldModalVisible(false);
              setItemToDeleteRepairWeld(null);
            }}
            onConfirm={handleConfirmDeleteRepairWeld}
            itemType="Repair Weld"
            itemName={itemToDeleteRepairWeld?.weldName || `Repair Weld ${itemToDeleteRepairWeld?.repairindex} (Weld ${itemToDeleteRepairWeld?.weldindex})`}
            isLoading={deleteRepairWeldLoading}
          />
        )}

        {/* PDF Viewer */}
        {viewerType === 'pdf' && viewerFile && (
          <PdfViewer
            fileUrl={viewerFile.fileUrl}
            fileName={viewerFile.fileName}
            onClose={closeViewer}
          />
        )}

        {/* Media Viewer */}
        {viewerType === 'media' && viewerFiles.length > 0 && (
          <MediaViewer
            files={viewerFiles}
            onClose={closeViewer}
          />
        )}

        {/* Document Viewer */}
        {viewerType === 'document' && viewerFile && (
          <DocumentViewer
            document={viewerFile}
            onClose={closeViewer}
          />
        )}
      </div>
    );
  }

  // Render specific views with back button
  // Build header details array
  const headerDetails = [
    { label: 'Assembly', value: selectedItem?.assemblyNumber ? `${selectedItem.assemblyNumber} - (${selectedPipe?.size}" × ${selectedPipe?.wallThickness}") Buckle Arrestor` : '-' },
    ...(project?.projectName ? [{ label: 'Project', value: project.projectName }] : []),
    ...(selectedHeadings?.wps?.label ? [{ label: 'WPS', value: selectedHeadings.wps.label }] : []),
    ...(selectedHeadings?.drawing?.label ? [{ label: 'Drawing', value: selectedHeadings.drawing.label }] : []),
    ...(selectedHeadings?.forge?.label ? [{ label: 'Forge', value: selectedHeadings.forge.label }] : []),
    ...(selectedHeadings?.pipe?.label ? [{ label: 'Pipe', value: selectedHeadings.pipe.label }] : []),
    ...(selectedHeadings?.wire?.label ? [{ label: 'Wire', value: selectedHeadings.wire.label }] : []),
    ...(selectedWeld === 0 ? [{ label: 'Weld', value: 'WELD 1' }] : []),
    ...(selectedWeld === 1 ? [{ label: 'Weld', value: 'WELD 2' }] : []),
    ...(selectedWeld === 0 && selectedHeadings?.welder1?.label ? [{ label: 'Welder ID', value: selectedHeadings.welder1.id === 0 ? 'I' : selectedHeadings.welder1.name }] : []),
    ...(selectedWeld === 1 && selectedHeadings?.welder2?.label ? [{ label: 'Welder ID', value: selectedHeadings.welder2.id === 0 ? 'I' : selectedHeadings.welder2.name }] : []),
    ...(selectedWeld === 0 && fitmentWeldData?.details?.fitmentDocId ? [{ label: 'Fitment', value: fitmentOptions.find((o: any) => String(o.value) === String(fitmentWeldData?.details?.fitmentDocId))?.label ?? 'Fitment' }] : []),
    ...(selectedWeld === 1 && fitmentWeldData?.details?.fitmentDocId ? [{ label: 'Fitment', value: fitmentOptions.find((o: any) => String(o.value) === String(fitmentWeldData?.details?.fitmentDocId))?.label ?? 'Fitment' }] : []),
  ];

  // Color palette for badges
  const colorPalette = [
    'bg-green-200', 'bg-blue-100', 'bg-green-100', 'bg-red-100',
    'bg-cyan-100', 'bg-teal-100', 'bg-indigo-100', 'bg-lime-100',
    'bg-pink-100', 'bg-rose-100', 'bg-emerald-100', 'bg-violet-100',
    'bg-amber-100', 'bg-sky-100', 'bg-stone-100', 'bg-slate-100',
    'bg-zinc-100', 'bg-purple-100', 'bg-orange-100', 'bg-yellow-100'
  ];

  // Convert details to infoItems with colors
  const infoItems = headerDetails.map((item, index) => ({
    ...item,
    bgColor: colorPalette[index % colorPalette.length]
  }));

  const visibleItems = showAllInfo ? infoItems : infoItems.slice(0, 7);

  return (
    <div className="fixed inset-0 bg-white z-100 overflow-y-auto">
      {/* Header with back button */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleBackNavigation}
              className="hover:bg-gray-100 p-2 rounded-lg transition-colors"
              title="Go back"
              variant="outline"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-semibold">
                {currentView === 'fitment' && 'Fitment'}
                {currentView === 'visual' && 'Visual Inspection'}
                {currentView === 'alignment' && 'Alignment'}
              </h2>
              <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1">
                <div className="flex flex-wrap gap-2">
                  {visibleItems.map((item, index) => (
                    <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                      {item.label}: {item.value}
                    </span>
                  ))}
                  {infoItems.length > 7 && (
                    <button
                      onClick={() => setShowAllInfo(!showAllInfo)}
                      className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                    >
                      {showAllInfo ? 'Show Less' : `Show More (+${infoItems.length - 7})`}
                      <span className="text-xs">{showAllInfo ? '▲' : '▼'}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">

            <Button
              onClick={handleBackNavigation}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              variant="outline"
            >
              Cancel
            </Button>
            {!isEditMode && hasSignatures() && canEdit && (
              <Button
                onClick={handleEdit}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                variant="outline"
                disabled={productionDataLoading || isReportLoading}
                loadingText="Generating..."
                loading={isReportLoading}
              >
                Edit
              </Button>
            )}


            {/* {hasFitmentSignatures() && (
              <Button
                onClick={handleDownloadFitmentReport}
                className="px-4 py-2 text-white bg-[#EB1C37] rounded-lg hover:bg-[#EB1C37]/90"
                variant="default"
                loading={fitmentReportDownloadLoading}
                loadingText="Generating..."
                disabled={fitmentReportDownloadLoading || hasUnsavedChanges}
              >
                <Download className="h-4 w-4 mr-2" />
                Download Report
              </Button>
            )} */}


            <Button
              // variant="outline"
              onClick={() => setIsDocumentApprovalModalOpen(true)}
              className="flex items-center gap-2"
              loading={isReportLoading}
              loadingText="Generating..."
              disabled={isReportLoading}
            >
              <Download className="h-4 w-4" />
              Download Report
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canEdit || !hasUnsavedChanges}
              className="px-4 py-2 bg-[#EB1C37] text-white rounded-lg hover:bg-[#EB1C37]/90"
              loading={
                currentView === 'fitment' ? fitmentUpdateLoading :
                  currentView === 'visual' ? visualInspectionUpdateLoading :
                    currentView === 'alignment' ? alignmentUpdateLoading : false
              }
              loadingText="Saving..."
            >
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Render specific content based on current view */}
        {currentView === 'fitment' && <Fitment
          selectedItem={selectedItem}
          selectedPipe={selectedPipe}
          materials={materials}
          specifications={specifications}
          fitmentWeldData={fitmentWeldData}
          fitmentState={fitmentState}
          onFitmentStateChange={handleFitmentStateChange}
          customerRepresentativeList={customerRepresentativeList}
          qcList={qcList}
          selectedHeadings={selectedHeadings}
          selectedWeld={selectedWeld}
          isEditMode={isEditMode}
          canEdit={canEdit}
          canReport={canReport}
          canSign={canSign}
          isCustomerRepresentativeUser={isCustomerRepresentativeUser}
          isQCInspectorUser={isQCInspectorUser}
          currentUserTeamMemberId={currentUserTeamMemberId}
          isAdminOrSuperAdmin={isAdminOrSuperAdmin}
        />}
        {currentView === 'visual' && <VisualInspection
          selectedItem={selectedItem}
          selectedPipe={selectedPipe}
          materials={materials}
          specifications={specifications}
          visualInspectionWeldData={visualInspectionWeldData}
          visualInspectionState={visualInspectionState}
          onVisualInspectionStateChange={handleVisualInspectionStateChange}
          weldData={productionSheetData?.productionWelds && productionSheetData.productionWelds[selectedWeld] ? productionSheetData.productionWelds[selectedWeld] : null}
          customerRepresentativeList={customerRepresentativeList}
          qcList={qcList}
          isEditMode={isEditMode}
          canEdit={canEdit}
          canReport={canReport}
          canSign={canSign}
          isCustomerRepresentativeUser={isCustomerRepresentativeUser}
          isQCInspectorUser={isQCInspectorUser}
          currentUserTeamMemberId={currentUserTeamMemberId}
          isAdminOrSuperAdmin={isAdminOrSuperAdmin}
        />}
        {currentView === 'alignment' && <Alignment
          selectedItem={selectedItem}
          selectedPipe={selectedPipe}
          materials={materials}
          specifications={specifications}
          alignmentWeldData={alignmentWeldData}
          alignmentState={alignmentState}
          onAlignmentStateChange={handleAlignmentStateChange}
          customerRepresentativeList={customerRepresentativeList}
          qcList={qcList}
          isEditMode={isEditMode}
          canEdit={canEdit}
          canReport={canReport}
          canSign={canSign}
          isCustomerRepresentativeUser={isCustomerRepresentativeUser}
          isQCInspectorUser={isQCInspectorUser}
          currentUserTeamMemberId={currentUserTeamMemberId}
          isAdminOrSuperAdmin={isAdminOrSuperAdmin}
        />}
      </div>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="max-w-[700px] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
            </div>
            <DialogTitle className="text-xl font-semibold">
              Unsaved Changes
            </DialogTitle>
          </div>
          <div>
            <p className="text-gray-700 mb-3">
              You have unsaved changes in the {currentView === 'fitment' ? 'Fitment' : currentView === 'visual' ? 'Visual Inspection' : 'Alignment'} tab. What would you like to do?
            </p>
            <div className="mt-4 p-3 bg-yellow-50 border rounded-md">
              <p className="text-sm text-yellow-700">
                <span className="font-semibold">Note:</span> Choose "Save" to keep your changes, "Discard" to remove changes and close, or "Cancel" to stay on this page.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={handleCancelNavigation}
              className="min-w-[100px]"
            >
              Cancel
            </Button>

            <Button
              onClick={handleSaveAndClose}
              className="min-w-[100px]"
              loading={
                currentView === 'fitment' ? fitmentUpdateLoading :
                  currentView === 'visual' ? visualInspectionUpdateLoading :
                    currentView === 'alignment' ? alignmentUpdateLoading : false
              }
              loadingText="Saving..."
              disabled={!hasUnsavedChanges && (fitmentUpdateLoading || visualInspectionUpdateLoading || alignmentUpdateLoading)}
            >
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button
              onClick={handleConfirmNavigation}
              className="min-w-[100px] bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Discard
              </div>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Confirmation Dialog - Shows when Edit button is clicked */}
      <Dialog open={showEditConfirmation} onOpenChange={setShowEditConfirmation}>
        <DialogContent className="max-w-[700px] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
            </div>
            <DialogTitle className="text-xl font-semibold">
              Edit Mode Confirmation
            </DialogTitle>
          </div>
          <div>
            <p className="text-gray-700 mb-3">
              If you make any changes, please add signatures after editing.
            </p>
            <div className="mt-4 p-3 bg-yellow-50 border rounded-md">
              <p className="text-sm text-yellow-700">
                <span className="font-semibold">Note:</span> After editing, you will need to add QC and Customer signatures before saving. Click "Yes" to proceed to edit mode, or "No" to stay in view mode.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={handleCancelEdit}
              className="min-w-[100px]"
            >
              No
            </Button>
            <Button
              variant="default"
              onClick={handleConfirmEdit}
              className="min-w-[100px] bg-[#EB1C37] hover:bg-[#EB1C37]/90 text-white"
            >
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Document Approval Modal */}
      {isDocumentApprovalModalOpen && productionSheetData && (
        <DocumentApprovalModal
          isOpen={isDocumentApprovalModalOpen}
          onClose={() => setIsDocumentApprovalModalOpen(false)}
          onOpenViewer={openViewer}
          productionSheetData={productionSheetData}
          selectedPipe={selectedPipe}
          assemblyNumber={selectedItem?.assemblyNumber}
          onDownloadMergedReport={async () => { }}
          onStatusChange={() => { }}
          onRefresh={loadProductionData}
          isFitmentreport={currentView === 'fitment'}
          isVisualInspectionreport={currentView === 'visual'}
          isAlignmentreport={currentView === 'alignment'}
          isProductionSheetreport={false}
          isNDTreport={false}
          isCoatingreport={false}
          isMergeProductionReport={false}
          isMergeReport={false}
          isVissibleApprovalButton={false}
        />
      )}

      {/* PDF Viewer */}
      {viewerType === 'pdf' && viewerFile && (
        <PdfViewer
          fileUrl={viewerFile.fileUrl}
          fileName={viewerFile.fileName}
          onClose={closeViewer}
        />
      )}

      {/* Media Viewer */}
      {viewerType === 'media' && viewerFiles.length > 0 && (
        <MediaViewer
          files={viewerFiles}
          onClose={closeViewer}
        />
      )}

      {/* Document Viewer */}
      {viewerType === 'document' && viewerFile && (
        <DocumentViewer
          document={viewerFile}
          onClose={closeViewer}
        />
      )}


    </div>
  );
}; 